import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/api';
import TopNav from '../components/TopNav';
import InvoicePrintEmailActions from '../components/InvoicePrintEmailActions';
import ConfirmModal from '../components/ConfirmModal';
import RepairCloseConfirm, { incompleteRepairServices } from '../components/RepairCloseConfirm';
import {
  invoiceEditTaxOptions,
  ShipDocInvoiceDocument,
  shippingInvoiceEditDraftFromDocument,
  TransferPrintLabel,
  printTransferLabel,
  transferToEmailInvoice,
} from './TransfersPage';
import { useAuth } from '../context/AuthContext';
import { can, hasPermission } from '../accessControl';
import {
  formatCalendarDate,
  formatDateTimeConsistent,
} from '../utils/dateDisplay';
import './css/InventoryPage.css';
import './css/TransfersPage.css';
import './css/InvoicesPage.css';
import './css/RepairOrdersPage.css';
import './css/ServiceRoomPage.css';

const autoEmailStatusText = (result) => result?.status === 'sent'
  ? ` PDF confirmation emailed to ${result.to}.`
  : result?.status === 'failed' ? ` ${result.message}` : '';

const emptyForm = {
  id: null,
  customerId: '',
  customerName: '',
  serial: '',
  customerSoldTo: '',
  notes: '',
  services: [],
  invoiceServices: [],
  status: 'Open',
  closedAt: null,
  closedBy: '',
};

const CUSTOM_SERVICE = {
  id: 'custom',
  itemId: null,
  custom: true,
  name: 'Repair',
  description: '',
  defaultPrice: 0,
  price: 0,
  defaultPriceType: 'amount',
  priceType: 'amount',
};

const DEFAULT_ORDER_STATUS_FILTER = 'Open';
const REPAIR_ORDER_STATUS_OPTIONS = ['Open', 'Closed'];
const REPAIR_ORDER_TRANSACTION_TYPE_ID = 640;

const money = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '$0.00';
  return number.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (value) => {
  return formatCalendarDate(value);
};

const formatDateTime = (value) => {
  return formatDateTimeConsistent(value, {
    timeZone: 'America/New_York',
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const LOCAL_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;

const formatLocalTimestampText = (value) => {
  if (!value) return '';
  const match = String(value).trim().match(LOCAL_TIMESTAMP_PATTERN);
  if (!match) return '';
  const [, year, month, day, hourText, minute, secondText = '00'] = match;
  const hour = Number(hourText);
  const second = Number(secondText);
  if (!Number.isFinite(hour) || !Number.isFinite(second)) return '';
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  const secondDisplay = second ? `:${String(second).padStart(2, '0')}` : '';
  return `${Number(month)}/${Number(day)}/${year}, ${hour12}:${minute}${secondDisplay} ${suffix}`;
};

const transactionTimestampDisplay = (value) => {
  const formatted = formatLocalTimestampText(value) || formatDateTime(value);
  if (!formatted) return { primary: '-', secondary: '' };
  const [primary, ...secondaryParts] = formatted.split(', ');
  return {
    primary,
    secondary: secondaryParts.join(', '),
  };
};

const renderTransactionTimestamp = (value) => {
  const display = transactionTimestampDisplay(value);
  return (
    <>
      {display.primary}
      {display.secondary && <small className="transfer-date-note">{display.secondary}</small>}
    </>
  );
};

const formatTransactionTimestampText = (value) => (
  formatLocalTimestampText(value) || formatDateTime(value)
);

const formatClosedDateTime = (value) => {
  if (!value) return null;
  const normalized = formatDateTimeConsistent(value, {
    timeZone: 'America/New_York',
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!normalized) return null;
  const [datePart, timePart = ''] = normalized.split(', ');
  return {
    date: datePart,
    time: timePart,
  };
};

const formatNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return Number.isInteger(number) ? String(number) : number.toFixed(2);
};

const servicePriceType = (service) => (
  String(service?.priceType || service?.defaultPriceType || '').toLowerCase() === 'percent' ? 'percent' : 'amount'
);

const isCreditCardFeeServiceName = (value) => /^other[-\s]*cc\s+fee$/i.test(String(value || '').trim());

const servicePriceLabel = (service, value = service?.defaultPrice ?? service?.price) => (
  servicePriceType(service) === 'percent' ? `${formatNumber(value)}%` : money(value)
);

const servicePriceInputValue = (service) => formatNumber(service?.price ?? service?.defaultPrice ?? 0);

const parseServicePriceInput = (value, fallback = 0) => {
  const text = String(value ?? '').replace(/[$,%]/g, '').trim();
  const number = Number(text);
  return Number.isFinite(number) ? number : Number(fallback || 0);
};

const parseOptionalMoneyInput = (value) => {
  const text = String(value ?? '').replace(/[$,\s]/g, '').trim();
  if (!text) return null;
  const number = Number(text);
  return Number.isFinite(number) && number >= 0 ? Math.round(number * 100) / 100 : null;
};

const getCustomerLabel = (customer) => customer?.name || customer?.full_name || customer?.fullname || '';

const isOtherRepairService = (service = {}) => {
  const name = String(service?.name || '').trim();
  return /^OTHER[-\s]/i.test(name) || /^DELIVERY$/i.test(name);
};

const hasServiceDescription = (service = {}) => (
  String(service?.description || '').trim().toLowerCase() === 'service'
);

const isCustomService = (service) => Boolean(service?.custom);

const isRepairOrderServiceOption = (service = {}) => (
  isCustomService(service) || !isOtherRepairService(service) || hasServiceDescription(service)
);

const getServiceKey = (service) => (
  service?.custom
    ? CUSTOM_SERVICE.id
    : String(service?.itemId || service?.id || service?.name || '')
);

const isCustomerReturnAdjustmentRow = (row = {}) => row.rowType === 'invoice_adjustment';

const customerReturnRowKey = (row = {}) => (
  isCustomerReturnAdjustmentRow(row)
    ? `adjustment-${row.id || row.adjustmentId || row.sourceDocNo || row.item}`
    : `item-${row.sourceDocNo || 'return'}-${row.itemId || row.serial}-${row.serial}`
);

const getServiceNameKey = (service) => String(service?.name || '').trim().toLowerCase();

const isOtherService = (service) => /^other[-\s]/i.test(String(service?.name || '').trim());

const serviceAmount = (service) => Number(service?.price) || 0;

const calculateServicesTotal = (serviceList = []) => {
  const baseTotal = serviceList.reduce((sum, service) => (
    servicePriceType(service) === 'percent' ? sum : sum + serviceAmount(service)
  ), 0);
  const percentTotal = serviceList.reduce((sum, service) => (
    servicePriceType(service) === 'percent' && isOtherService(service)
      ? sum + ((isCreditCardFeeServiceName(service.name) && service.serviceBaseAmount !== null && service.serviceBaseAmount !== undefined
        ? Number(service.serviceBaseAmount) || 0
        : baseTotal) * serviceAmount(service) / 100)
      : sum
  ), 0);
  const fixedPercentFallback = serviceList.reduce((sum, service) => (
    servicePriceType(service) === 'percent' && !isOtherService(service)
      ? sum + serviceAmount(service)
      : sum
  ), 0);
  return baseTotal + percentTotal + fixedPercentFallback;
};

const isManualGroupAssignmentService = (service) => (
  isCustomService(service) || getServiceNameKey(service) === getServiceNameKey(CUSTOM_SERVICE)
);

const getServiceSubServiceNames = (service) => {
  const expandDefinition = (value) => {
    const text = String(value || '').trim();
    if (/^\d+$/.test(text)) {
      return Array.from({ length: Math.max(0, Number(text)) }, (_, index) => `Step ${index + 1}`);
    }
    return String(value || '').split(',');
  };
  const source = Array.isArray(service?.subServiceNames)
    ? service.subServiceNames
    : (Array.isArray(service?.sub_service_names)
      ? service.sub_service_names
      : expandDefinition(service?.subServices ?? service?.sub_services ?? ''));
  return [...new Set(source.map((name) => String(name || '').trim()).filter(Boolean))];
};

const getServiceGroupAssignments = (service) => (
  Array.isArray(service?.groupAssignments) ? service.groupAssignments : []
);

const getServiceGroupAssignment = (service, subService = null) => (
  getServiceGroupAssignments(service).find((assignment) => (
    String(assignment.subService ?? '') === String(subService ?? '')
  )) || null
);

const getServiceTasks = (service) => (
  Array.isArray(service?.tasks)
    ? service.tasks
    : (Array.isArray(service?.taskProgress) ? service.taskProgress : [])
);

const getServiceTask = (service, subService = null) => {
  const normalizedSubService = String(subService ?? '');
  return getServiceTasks(service).find((task) => (
    String(task.subService ?? '') === normalizedSubService
  )) || null;
};

const groupContainsService = (group, service, subService = null) => {
  if (isManualGroupAssignmentService(service)) return true;
  const serviceItemId = String(service?.itemId || service?.id || '');
  const serviceName = getServiceNameKey(service);
  return (group?.services || []).some((groupService) => {
    const itemMatches = serviceItemId && String(groupService.itemId || groupService.id || '') === serviceItemId;
    const nameMatches = serviceName && getServiceNameKey(groupService) === serviceName;
    if (!itemMatches && !nameMatches) return false;
    const selectedSubServices = Array.isArray(groupService.selectedSubServices)
      ? groupService.selectedSubServices
      : (Array.isArray(groupService.selected_sub_services) ? groupService.selected_sub_services : []);
    if (subService === null || subService === undefined) return true;
    return selectedSubServices.map(String).includes(String(subService));
  });
};

const getGroupId = (group) => group?.groupId || group?.id || '';

const getAssignableGroupRows = (service) => (
  getServiceSubServiceNames(service).length > 0
    ? getServiceSubServiceNames(service)
    : [null]
);

const getServiceTaskRows = (service) => (
  getAssignableGroupRows(service).map((subService) => {
    const task = getServiceTask(service, subService);
    return {
      key: String(subService ?? 'service'),
      subService,
      task,
      status: task?.status || service.serviceStatus || service.status || 'Pending',
      inProgressAt: task ? (task.inProgressAt || '') : (service.serviceInProgressAt || ''),
      completedAt: task?.completedAt || service.serviceCompletedAt || '',
      worker: task?.worker || service.worker || service.serviceWorker || '',
    };
  })
);

const getGroupOptionsForService = (groups, service, subService = null) => (
  isManualGroupAssignmentService(service)
    ? groups
    : groups.filter((group) => groupContainsService(group, service, subService))
);

const buildAutomaticGroupAssignments = (service, groups) => {
  if (isOtherService(service)) {
    return [];
  }
  if (isManualGroupAssignmentService(service)) {
    return getServiceGroupAssignments(service);
  }
  return getAssignableGroupRows(service)
    .map((subService) => {
      const existing = getServiceGroupAssignment(service, subService);
      const options = getGroupOptionsForService(groups, service, subService);
      const matchingExisting = options.find((group) => String(getGroupId(group)) === String(existing?.groupId || ''));
      const group = matchingExisting || options[0];
      if (!group) return null;
      return {
        subService,
        groupId: Number(getGroupId(group)),
        groupName: group.name || '',
      };
    })
    .filter(Boolean);
};

const withAutomaticGroupAssignments = (service, groups) => ({
  ...service,
  groupAssignments: buildAutomaticGroupAssignments(service, groups),
});

const getCustomerId = (customer) => customer?.id || customer?.nc_customer_id || customer?.nccustomerid || '';

const getLocationId = (location) => location?.id || location?.loc_id || '';

const getLocationName = (location) => location?.name || location?.loc_location || '';

const normalizeMatchText = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const normalizeCustomerTypeName = (value) => String(value || '').trim().toLowerCase();

const getCustomerTypeName = (customer, types = []) => {
  const directName = customer?.customerTypeName
    || customer?.customer_type_name
    || customer?.typeName
    || customer?.type_name;
  if (directName) return directName;

  const typeId = customer?.customertypeid
    || customer?.customerTypeId
    || customer?.customer_type_id
    || customer?.type;
  const matchedType = types.find((type) => String(type.id) === String(typeId));
  return matchedType?.name || '';
};

const isRetailOrCodCustomer = (customer, types = []) => {
  const typeName = normalizeCustomerTypeName(getCustomerTypeName(customer, types));
  return typeName === 'retail' || typeName === 'cod';
};

const findLocationByName = (locations = [], name = '') => locations.find((location) => (
  normalizeMatchText(getLocationName(location)) === normalizeMatchText(name)
));

const findUsableLocationByName = (locations = [], name = '') => {
  const matches = locations.filter((location) => (
    normalizeMatchText(getLocationName(location)) === normalizeMatchText(name)
  ));
  return matches.find((location) => location.active !== false && location.deleted !== true)
    || matches.find((location) => location.active !== false)
    || matches[0]
    || null;
};

const isNoSerialBypass = (value) => {
  const text = String(value || '').trim();
  return text.toLowerCase() === 'nosn' || /^RO-\d+$/i.test(text);
};
const statusClassName = (status) => String(status || 'Pending')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');
const DEFAULT_CUSTOMER_RETURN_LOCATION = 'IRENES WIGS - NEW YORK';
const ENABLE_REPAIR_OPEN_ORDER_LOOKUP = false;
const SHOW_CUSTOMER_RETURN_LOCATION_DROPDOWN = false;
const CURRENT_YEAR = new Date().getFullYear();
const START_YEAR = 2014;
const ALL_YEARS_VALUE = 'all';
const RECENT_REPAIR_SEARCH_YEARS_VALUE = 'recent';
const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - START_YEAR + 1 },
  (_, index) => String(CURRENT_YEAR - index)
);

const nextFrame = () => new Promise((resolve) => {
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    window.requestAnimationFrame(() => resolve());
    return;
  }
  setTimeout(resolve, 0);
});

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const CODE_128_PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
  '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
  '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
  '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
  '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
  '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
  '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
  '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
  '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
  '211214', '211232', '2331112',
];

const code128BValue = (char) => {
  const code = char.charCodeAt(0);
  if (code < 32 || code > 127) {
    throw new Error('Barcode supports standard letters, numbers, and punctuation only.');
  }
  return code - 32;
};

const createCode128Svg = (value) => {
  const text = String(value || '').trim();
  if (!text) throw new Error('Serial number is required for barcode printing.');
  const values = [...text].map(code128BValue);
  const checksum = (104 + values.reduce((sum, code, index) => sum + (code * (index + 1)), 0)) % 103;
  const sequence = [104, ...values, checksum, 106];
  const moduleWidth = 2;
  const height = 74;
  let x = 0;
  const rects = [];

  sequence.forEach((code) => {
    CODE_128_PATTERNS[code].split('').forEach((widthText, index) => {
      const width = Number(widthText) * moduleWidth;
      if (index % 2 === 0) {
        rects.push(`<rect x="${x}" y="0" width="${width}" height="${height}" />`);
      }
      x += width;
    });
  });

  return `<svg class="repair-barcode-svg" xmlns="http://www.w3.org/2000/svg" width="${x}" height="${height}" viewBox="0 0 ${x} ${height}" role="img" aria-label="Barcode ${escapeHtml(text)}">${rects.join('')}</svg>`;
};

const getRepairInvoiceBarcode = (invoice = {}) => {
  const item = invoice.items?.[0] || {};
  return {
    serial: String(item.serial || '').trim(),
    description: String(invoice.itemDescription || item.description || 'Repair Order').trim(),
  };
};

const parseSerialSearch = (value) => String(value || '')
  .split(/[,\n]+/)
  .map((serial) => serial.trim())
  .filter(Boolean);

const RepairBarcodePreview = ({ invoice }) => {
  const { serial, description } = getRepairInvoiceBarcode(invoice);
  if (!serial) return <p className="invoice-modal-status error">No serial number is available for this barcode.</p>;
  return (
    <div className="transfer-output-preview repair-barcode-preview">
      <section className="repair-barcode-label">
        <div className="repair-barcode-description">{description}</div>
        <div dangerouslySetInnerHTML={{ __html: createCode128Svg(serial) }} />
        <div className="repair-barcode-serial">{serial}</div>
      </section>
    </div>
  );
};

const getRetailCustomerTypeId = (types = []) => {
  const retail = types.find((type) => String(type.name || '').trim().toLowerCase() === 'retail');
  return retail ? String(retail.id) : '';
};

const getCustomerPhone = (customer) => customer?.phone || customer?.altphone || customer?.alt_phone || '';

const buildAddressLines = (source = {}, prefix = '') => {
  const lines = [
    source[`${prefix}addr1`] || source[`${prefix}_addr_1`] || source.addr1 || source.address1,
    source[`${prefix}addr2`] || source[`${prefix}_addr_2`] || source.addr2 || source.address2,
    source[`${prefix}addr3`] || source[`${prefix}_addr_3`] || source.addr3 || source.address3,
    source[`${prefix}addr4`] || source[`${prefix}_addr_4`] || source.addr4 || source.address4,
    [
      source[`${prefix}city`] || source[`${prefix}_city`] || source.city,
      source[`${prefix}state`] || source[`${prefix}_state`] || source.state,
      source[`${prefix}zip`] || source[`${prefix}_zip`] || source.zip,
    ].filter(Boolean).join(' '),
    source[`${prefix}country`] || source[`${prefix}_country`] || source.country,
  ];
  return lines.map((line) => String(line || '').trim()).filter(Boolean);
};

const getCustomerAddressLines = (customer = {}) => {
  const billingLines = buildAddressLines(customer, 'bill');
  if (billingLines.length > 0) return billingLines;
  const shippingLines = buildAddressLines(customer, 'ship');
  if (shippingLines.length > 0) return shippingLines;
  const address = Array.isArray(customer.addresses) ? customer.addresses[0] : null;
  return address ? buildAddressLines(address) : [];
};

const phoneDigits = (value) => String(value || '').replace(/\D/g, '');

const isPhoneLine = (value) => {
  const text = String(value || '').trim();
  const digits = phoneDigits(text);
  return digits.length >= 7 && /^[\d\s().+\-xext]+$/i.test(text);
};

const getSelectedCustomerDetails = (customer) => {
  const name = getCustomerLabel(customer);
  const rawAddressLines = getCustomerAddressLines(customer);
  const rawPhone = getCustomerPhone(customer);
  const phone = rawPhone || rawAddressLines.find(isPhoneLine) || '';
  const nameKey = normalizeMatchText(name);
  const phoneKey = phoneDigits(phone);
  const seenAddressLines = new Set();
  const addressLines = rawAddressLines.filter((line) => {
    const lineKey = normalizeMatchText(line);
    const linePhoneKey = phoneDigits(line);
    if (!lineKey) return false;
    if (lineKey === nameKey) return false;
    if (isPhoneLine(line)) return false;
    if (phoneKey && linePhoneKey === phoneKey) return false;
    if (seenAddressLines.has(lineKey)) return false;
    seenAddressLines.add(lineKey);
    return true;
  });

  return { name, phone, addressLines };
};

const renderCustomerOption = (customer, tooltipHandlers = {}) => {
  const phone = getCustomerPhone(customer);
  const addressLines = getCustomerAddressLines(customer);
  const addressText = addressLines.length > 0 ? addressLines.join('\n') : 'No address on file.';
  return (
    <span className="repair-customer-option">
      <span className="repair-customer-option-main">
        <span>{getCustomerLabel(customer) || '(No name)'}</span>
        <span
          className="repair-customer-address-info"
          aria-label={`Customer address: ${addressText}`}
          tabIndex={-1}
          onMouseEnter={(event) => tooltipHandlers.show?.(event, addressLines)}
          onMouseLeave={() => tooltipHandlers.hide?.()}
        >
          i
        </span>
      </span>
      {phone && <small>{phone}</small>}
    </span>
  );
};

function SearchSelect({
  value,
  onChange,
  options,
  placeholder,
  label,
  getLabel,
  getSearchText,
  onSelect,
  renderOption,
  className = '',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [tooltip, setTooltip] = useState(null);
  const query = String(value || '').trim().toLowerCase();
  const getOptionSearchText = getSearchText || getLabel;
  const visible = options
    .filter((option) => !query || String(getOptionSearchText(option) || '').toLowerCase().includes(query))
    .slice(0, 40);
  const menuId = `repair-typeahead-${String(label || placeholder || 'options').replace(/\s+/g, '-').toLowerCase()}`;

  const choose = (option) => {
    if (!option) return;
    setTooltip(null);
    onSelect(option);
    setOpen(false);
    setActiveIndex(0);
  };

  const showTooltip = (event, lines = []) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      lines: lines.length > 0 ? lines : ['No address on file.'],
      top: rect.bottom + 6,
      left: rect.left,
    });
  };

  const hideTooltip = () => setTooltip(null);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => (
        visible.length ? Math.min(current + 1, visible.length - 1) : 0
      ));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(0);
      return;
    }

    if (event.key === 'Enter') {
      if (open && visible[activeIndex]) {
        event.preventDefault();
        choose(visible[activeIndex]);
      }
      return;
    }

    if (event.key === 'Tab' && open && visible[activeIndex]) {
      choose(visible[activeIndex]);
    }
  };

  return (
    <div className={`transfer-typeahead repair-typeahead ${className}`}>
      <input
        className="transfer-filter-input"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onFocus={() => {
          setOpen(true);
          setActiveIndex(0);
        }}
        onBlur={() => setTimeout(() => {
          setOpen(false);
          setTooltip(null);
        }, 120)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={!disabled && open}
        aria-controls={menuId}
      />
      {!disabled && open && visible.length > 0 && (
        <div className="transfer-typeahead-menu" id={menuId} role="listbox">
          {visible.map((option, index) => (
            <button
              key={option.id}
              type="button"
              className={index === activeIndex ? 'active' : ''}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option);
              }}
              role="option"
              aria-selected={index === activeIndex}
            >
              {renderOption ? renderOption(option, { show: showTooltip, hide: hideTooltip }) : getLabel(option)}
            </button>
          ))}
        </div>
      )}
      {tooltip && (
        <div
          className="repair-customer-address-tooltip"
          role="tooltip"
          style={{ top: tooltip.top, left: tooltip.left }}
        >
          {tooltip.lines.map((line) => <span key={line}>{line}</span>)}
        </div>
      )}
    </div>
  );
}

function RepairOrdersPage({
  pageTitle = 'Repair Orders',
  newTabLabel = 'New Order',
  listTabLabel = 'Repair Orders',
  singularLabel = 'Repair Order',
  pluralLabel = 'repair orders',
  listEndpoint = '/repair-orders',
  listPrimaryColumnLabel = 'RO #',
  listWarrantyColumnLabel = 'Warranty',
  allowListSelection = true,
  listDocumentTransactionTypeId = null,
  customerReturnMode = false,
} = {}) {
  const { user } = useAuth();
  const canReadLocations = hasPermission(user, 'locations.read');
  const canOpenCustomerReturn = hasPermission(user, 'returns.create') || hasPermission(user, 'returns.read');
  const canCreateOrder = customerReturnMode ? canOpenCustomerReturn : can(user, 'repairs.create');
  const canEditOrder = customerReturnMode ? canOpenCustomerReturn : can(user, 'repairs.edit');
  const canDeleteOrder = !customerReturnMode && can(user, 'repairs.delete');
  const canCreateRetailCustomer = can(user, 'customers.create', { customerTypeName: 'Retail', typeName: 'Retail' });
  const [activeTab, setActiveTab] = useState('new');
  const [customers, setCustomers] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const linkedOrderLoadedRef = useRef(false);
  const [orderLoading, setOrderLoading] = useState(() => (
    !customerReturnMode && listEndpoint === '/repair-orders'
    && /^\d+$/.test(new URLSearchParams(window.location.search).get('orderId') || '')
  ));
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [retailCustomerModalOpen, setRetailCustomerModalOpen] = useState(false);
  const [newRetailCustomer, setNewRetailCustomer] = useState({ companyname: '', phone: '', email: '' });
  const [retailCustomerStatus, setRetailCustomerStatus] = useState('');
  const [retailDuplicatePhoneCustomers, setRetailDuplicatePhoneCustomers] = useState([]);
  const [retailCustomerSaving, setRetailCustomerSaving] = useState(false);
  const [serialInfo, setSerialInfo] = useState(null);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [serviceModalTab, setServiceModalTab] = useState('repairs');
  const [modalServiceIds, setModalServiceIds] = useState([]);
  const [modalServiceValues, setModalServiceValues] = useState({});
  const [modalServiceBaseAmounts, setModalServiceBaseAmounts] = useState({});
  const [modalCustomServiceCount, setModalCustomServiceCount] = useState(0);
  const [serviceSearch, setServiceSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [serialResults, setSerialResults] = useState([]);
  const [serialLoading, setSerialLoading] = useState(false);
  const isRepairOrdersList = !customerReturnMode && listEndpoint === '/repair-orders';
  const [orderYear, setOrderYear] = useState(isRepairOrdersList ? String(CURRENT_YEAR) : ALL_YEARS_VALUE);
  const [orderListLoading, setOrderListLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => new Set([DEFAULT_ORDER_STATUS_FILTER]));
  const [openStatusFilter, setOpenStatusFilter] = useState(false);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [serviceStatusSaving, setServiceStatusSaving] = useState(false);
  const [repairCloseConfirm, setRepairCloseConfirm] = useState(null);

  const confirmIncompleteRepairs = () => {
    const services = incompleteRepairServices(form.services);
    if (!services.length) return Promise.resolve(true);
    return new Promise(resolve => setRepairCloseConfirm({ services, resolve }));
  };

  const answerRepairClose = answer => {
    repairCloseConfirm?.resolve(answer);
    setRepairCloseConfirm(null);
  };
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, orderId: null });
  const [persistedOrderStatus, setPersistedOrderStatus] = useState('');
  const [invoiceModal, setInvoiceModal] = useState({ invoice: null, loading: false, error: '' });
  const [repairInvoicePrintView, setRepairInvoicePrintView] = useState('invoice');
  const [transferDocumentModal, setTransferDocumentModal] = useState({ transfer: null, loading: false, error: '' });
  const [creditMemoInvoiceEditor, setCreditMemoInvoiceEditor] = useState({ open: false, docNo: '', draft: null, saving: false, error: '' });
  const [invoiceEmailLoading, setInvoiceEmailLoading] = useState(false);
  const [repairLogModal, setRepairLogModal] = useState({ open: false, loading: false, error: '', events: [] });
  const [customerReturnQueue, setCustomerReturnQueue] = useState([]);
  const [customerReturnMemo, setCustomerReturnMemo] = useState('');
  const [customerReturnLocation, setCustomerReturnLocation] = useState('');
  const [customerReturnValidatedSerial, setCustomerReturnValidatedSerial] = useState('');
  const [customerReturnLocationChoice, setCustomerReturnLocationChoice] = useState({
    open: false,
    customer: null,
    customerLocationName: '',
  });
  const lastSerialLookupRef = useRef('');
  const statusClearTimeoutRef = useRef(null);
  const skipTransactionUnloadPromptRef = useRef(false);
  const [leaveConfirm, setLeaveConfirm] = useState({
    open: false,
    onConfirm: () => {},
  });

  const queuedTransactionCount = activeTab === 'new'
    ? (customerReturnMode ? customerReturnQueue.length : (!form.id ? form.services.length : 0))
    : 0;

  useEffect(() => {
    document.title = `${pageTitle} - Services Irene's Wigs.com`;
  }, [pageTitle]);

  const clearQueuedTransaction = useCallback(() => {
    setForm(emptyForm);
    setSerialInfo(null);
    setCustomerReturnQueue([]);
    setCustomerReturnMemo('');
    setCustomerReturnLocation('');
    setCustomerReturnValidatedSerial('');
    setPersistedOrderStatus('');
    setStatus('');
  }, []);

  const confirmLeavingTransaction = useCallback((onConfirm) => {
    setLeaveConfirm({ open: true, onConfirm });
  }, []);

  useEffect(() => {
    if (queuedTransactionCount === 0) return undefined;

    const handleBeforeUnload = (event) => {
      if (skipTransactionUnloadPromptRef.current) return undefined;
      event.preventDefault();
      event.returnValue = 'Current Transaction will be lost.';
      return event.returnValue;
    };

    const handlePageLinkClick = (event) => {
      const link = event.target.closest?.('a');
      if (!link || !link.href) return;
      if (link.target && link.target !== '_self') return;
      if (link.origin !== window.location.origin) return;

      event.preventDefault();
      confirmLeavingTransaction(() => {
        skipTransactionUnloadPromptRef.current = true;
        clearQueuedTransaction();
        window.location.assign(link.href);
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handlePageLinkClick, true);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handlePageLinkClick, true);
    };
  }, [clearQueuedTransaction, confirmLeavingTransaction, queuedTransactionCount]);

  const changeActiveTab = (tab) => {
    const switchTab = () => {
      setActiveTab(tab);
      if (tab === 'orders') {
        loadOrders().catch((error) => {
          setStatus(error.response?.data?.error || error.message || `Failed to load ${pluralLabel}.`);
        });
      }
    };

    if (tab !== activeTab && queuedTransactionCount > 0) {
      confirmLeavingTransaction(() => {
        clearQueuedTransaction();
        setLeaveConfirm({ open: false, onConfirm: () => {} });
        switchTab();
      });
      return;
    }
    switchTab();
  };

  const invoiceServiceTotals = useMemo(() => {
    let runningTotal = Math.round(calculateServicesTotal(form.services) * 100) / 100;
    const adjustments = (form.invoiceServices || []).map((service) => {
      const base = /^other[-\s]*(cc\s+fee|payment\s+discount)$/i.test(service.name || '')
        ? Number(service.baseAmount ?? runningTotal) : runningTotal;
      const amount = Math.round((service.priceType === 'percent'
        ? base * Number(service.price) / 100 : Number(service.price)) * 100) / 100;
      runningTotal = Math.round((runningTotal + amount) * 100) / 100;
      return { ...service, calculatedAmount: amount };
    });
    return { adjustments, total: runningTotal };
  }, [form.services, form.invoiceServices]);
  const total = invoiceServiceTotals.total;
  const isClosedOrder = Boolean(form.id && persistedOrderStatus === 'Closed');
  const closedDateTime = isClosedOrder ? formatClosedDateTime(form.closedAt) : null;
  const newOrderTabLabel = !customerReturnMode && (form.id || orderLoading) ? 'Existing Order' : newTabLabel;
  const serviceOptions = useMemo(() => [CUSTOM_SERVICE, ...services], [services]);
  const repairServiceOptions = useMemo(
    () => serviceOptions.filter((service) => isCustomService(service) || !isOtherRepairService(service)),
    [serviceOptions]
  );
  const otherRepairServiceOptions = useMemo(
    () => serviceOptions.filter((service) => (
      !isCustomService(service) && isOtherRepairService(service) && hasServiceDescription(service)
    )),
    [serviceOptions]
  );
  const selectedRepairServiceCount = useMemo(
    () => modalCustomServiceCount + repairServiceOptions.filter((service) => (
      !isCustomService(service) && modalServiceIds.includes(getServiceKey(service))
    )).length,
    [modalCustomServiceCount, modalServiceIds, repairServiceOptions]
  );
  const selectedOtherRepairServiceCount = useMemo(
    () => otherRepairServiceOptions.filter((service) => modalServiceIds.includes(getServiceKey(service))).length,
    [modalServiceIds, otherRepairServiceOptions]
  );
  const activeServiceModalOptions = serviceModalTab === 'repairs'
    ? repairServiceOptions
    : otherRepairServiceOptions;
  const availableServiceOptions = useMemo(() => {
    const selectedNames = new Set(form.services.map(getServiceNameKey));
    return serviceOptions.filter((service) => (
      isRepairOrderServiceOption(service)
      && (isCustomService(service) || !selectedNames.has(getServiceNameKey(service)))
    ));
  }, [form.services, serviceOptions]);
  useEffect(() => {
    if (serviceGroups.length === 0) return;
    setForm((current) => {
      if (current.services.length === 0) return current;
      const servicesWithGroups = current.services.map((service) => withAutomaticGroupAssignments(service, serviceGroups));
      const currentAssignments = current.services.map((service) => getServiceGroupAssignments(service));
      const nextAssignments = servicesWithGroups.map((service) => getServiceGroupAssignments(service));
      if (JSON.stringify(currentAssignments) === JSON.stringify(nextAssignments)) return current;
      return { ...current, services: servicesWithGroups };
    });
  }, [serviceGroups]);
  const preparedBy = user?.name || user?.username || '';
  const defaultCustomerReturnLocation = useMemo(() => (
    findLocationByName(locations, DEFAULT_CUSTOMER_RETURN_LOCATION) || null
  ), [locations]);
  const selectedCustomer = useMemo(() => (
    customers.find((customer) => {
      const customerId = String(getCustomerId(customer));
      const customerName = normalizeMatchText(getCustomerLabel(customer));
      return (
        (form.customerId && customerId === String(form.customerId))
        || (form.customerName && customerName === normalizeMatchText(form.customerName))
      );
    }) || null
  ), [customers, form.customerId, form.customerName]);
  const canCreateCustomerReturn = customerReturnMode
    ? can(user, 'returns.create', selectedCustomer || null)
    : false;
  const selectedCustomerDetails = useMemo(
    () => (selectedCustomer ? getSelectedCustomerDetails(selectedCustomer) : null),
    [selectedCustomer]
  );
  const isSelectedCustomerRetailOrCod = useMemo(
    () => Boolean(selectedCustomer && isRetailOrCodCustomer(selectedCustomer, customerTypes)),
    [customerTypes, selectedCustomer]
  );
  const selectedCustomerLocation = useMemo(() => {
    if (!selectedCustomer) return null;
    const customerNames = [
      getCustomerLabel(selectedCustomer),
      selectedCustomer.name,
      selectedCustomer.full_name,
      selectedCustomer.fullname,
    ].map((value) => String(value || '').trim()).filter(Boolean);
    return locations.find((location) => customerNames.some((name) => (
      normalizeMatchText(getLocationName(location)) === normalizeMatchText(name)
    ))) || null;
  }, [locations, selectedCustomer]);
  const customerReturnLocationOptions = useMemo(() => {
    const defaultLocationName = getLocationName(defaultCustomerReturnLocation) || DEFAULT_CUSTOMER_RETURN_LOCATION;
    if (isSelectedCustomerRetailOrCod) {
      return [{ value: defaultLocationName, label: defaultLocationName }];
    }

    const options = [];
    const customerLocationName = getLocationName(selectedCustomerLocation);
    if (customerLocationName) {
      options.push({ value: customerLocationName, label: customerLocationName });
    }
    if (!options.some((option) => normalizeMatchText(option.value) === normalizeMatchText(defaultLocationName))) {
      options.push({ value: defaultLocationName, label: defaultLocationName });
    }
    return options;
  }, [defaultCustomerReturnLocation, isSelectedCustomerRetailOrCod, selectedCustomerLocation]);
  const customerReturnQueueTotalQty = useMemo(() => customerReturnQueue.reduce(
    (sum, row) => sum + (isCustomerReturnAdjustmentRow(row) ? 0 : (Number(row.qty ?? row.quantity ?? 1) || 1)),
    0
  ), [customerReturnQueue]);
  const customerReturnQueueTotalPrice = useMemo(() => customerReturnQueue.reduce((sum, row) => {
    const qty = isCustomerReturnAdjustmentRow(row) ? 1 : (Number(row.qty ?? row.quantity ?? 1) || 1);
    const price = Number(row.price) || 0;
    return sum + (qty * price);
  }, 0), [customerReturnQueue]);
  const customerReturnQueuedItemCount = useMemo(
    () => customerReturnQueue.filter((row) => !isCustomerReturnAdjustmentRow(row)).length,
    [customerReturnQueue]
  );
  const isCustomerReturnLocked = customerReturnQueue.length > 0;
  const lockedCustomerReturnLocation = customerReturnQueue[0]?.returnLocation || customerReturnLocation;
  const isCustomerReturnSerialKnownUnsold = Boolean(
    customerReturnMode
    && serialInfo
    && normalizeMatchText(serialInfo.serial) === normalizeMatchText(form.serial)
    && !serialInfo.sold
  );
  const isCustomerReturnSerialValidated = Boolean(
    customerReturnMode
    && form.serial
    && normalizeMatchText(customerReturnValidatedSerial) === normalizeMatchText(form.serial)
  );
  const isCustomerReturnWaitingForValidation = Boolean(
    customerReturnMode && !isCustomerReturnSerialValidated
  );
  const statusOptions = useMemo(() => (
    isRepairOrdersList
      ? REPAIR_ORDER_STATUS_OPTIONS
      : [...new Set(orders.map((order) => order.status).filter(Boolean))].sort()
  ), [isRepairOrdersList, orders]);
  const hasOrderSearch = String(orderSearch || '').trim() !== '';
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => (
      (isRepairOrdersList && hasOrderSearch)
      || (statusFilter.size === 0 || statusFilter.has(order.status))
    ));
  }, [orders, statusFilter, isRepairOrdersList, hasOrderSearch]);
  const serialCurrentLocation = serialResults.find((row) => row.currentLocation)?.currentLocation || '';

  useEffect(() => {
    setStatusFilter((current) => {
      if (statusOptions.length === 0) return current;
      if (current.size === 0) return current;
      const available = new Set(statusOptions);
      const next = new Set([...current].filter((value) => available.has(value)));
      if (next.size === current.size) return current;
      return next;
    });
  }, [statusOptions]);

  useEffect(() => () => {
    if (statusClearTimeoutRef.current) {
      clearTimeout(statusClearTimeoutRef.current);
    }
  }, []);

  const statusRequestValue = (statuses) => {
    if (!isRepairOrdersList || !statuses || statuses.size === 0 || statuses.size === statusOptions.length) return 'all';
    if (statuses.has('Closed')) return 'closed';
    return 'open';
  };

  const showTemporaryStatus = (message, duration = 3500) => {
    if (statusClearTimeoutRef.current) {
      clearTimeout(statusClearTimeoutRef.current);
    }
    setStatus(message);
    statusClearTimeoutRef.current = setTimeout(() => {
      setStatus((current) => (current === message ? '' : current));
      statusClearTimeoutRef.current = null;
    }, duration);
  };

  const setDefaultCustomerReturnLocation = useCallback(() => {
    setCustomerReturnLocation(getLocationName(defaultCustomerReturnLocation) || DEFAULT_CUSTOMER_RETURN_LOCATION);
  }, [defaultCustomerReturnLocation]);

  const closeCustomerReturnLocationChoice = useCallback(() => {
    setCustomerReturnLocationChoice({
      open: false,
      customer: null,
      customerLocationName: '',
    });
  }, []);

  const chooseCustomerReturnLocation = async (locationName, { queueCurrentSerial = false } = {}) => {
    if (locationName) {
      setCustomerReturnLocation(locationName);
    }
    closeCustomerReturnLocationChoice();
    if (queueCurrentSerial) {
      await queueCustomerReturnSerial({ locationOverride: locationName });
    }
  };

  const findCustomerLocationName = useCallback((customer) => {
    if (!customer) return '';
    const customerNames = [
      getCustomerLabel(customer),
      customer.name,
      customer.full_name,
      customer.fullname,
    ].map((value) => String(value || '').trim()).filter(Boolean);
    const location = locations.find((candidate) => customerNames.some((name) => (
      normalizeMatchText(getLocationName(candidate)) === normalizeMatchText(name)
    )));
    return getLocationName(location);
  }, [locations]);

  const promptCustomerReturnLocation = useCallback((customer) => {
    if (!customerReturnMode || !customer) return;
    if (customerReturnQueue.length > 0) {
      closeCustomerReturnLocationChoice();
      return;
    }
    if (isRetailOrCodCustomer(customer, customerTypes)) {
      setDefaultCustomerReturnLocation();
      closeCustomerReturnLocationChoice();
      return;
    }

    setCustomerReturnLocationChoice({
      open: true,
      customer,
      customerLocationName: findCustomerLocationName(customer),
    });
  }, [
    closeCustomerReturnLocationChoice,
    customerReturnQueue.length,
    customerReturnMode,
    customerTypes,
    findCustomerLocationName,
    setDefaultCustomerReturnLocation,
  ]);

  useEffect(() => {
    if (!customerReturnMode) return;
    if (isCustomerReturnLocked) {
      closeCustomerReturnLocationChoice();
      return;
    }

    if (isSelectedCustomerRetailOrCod) {
      setDefaultCustomerReturnLocation();
      return;
    }

    setCustomerReturnLocation((current) => {
      if (current && customerReturnLocationOptions.some((option) => option.value === current)) {
        return current;
      }
      return customerReturnLocationOptions[0]?.value || '';
    });
  }, [
    customerReturnLocationOptions,
    customerReturnMode,
    isCustomerReturnLocked,
    isSelectedCustomerRetailOrCod,
    closeCustomerReturnLocationChoice,
    setDefaultCustomerReturnLocation,
  ]);

  const loadOrders = async (query = orderSearch, year = orderYear, statuses = statusFilter) => {
    const searchText = String(query || '').trim();
    const requestYear = searchText
      ? (isRepairOrdersList ? RECENT_REPAIR_SEARCH_YEARS_VALUE : ALL_YEARS_VALUE)
      : year;
    setOrderListLoading(true);
    if (searchText) setOrders([]);
    try {
      const params = { q: searchText, year: requestYear };
      if (isRepairOrdersList) params.status = searchText ? 'all' : statusRequestValue(statuses);
      const response = await api.get(listEndpoint, { params });
      const rows = response.data || [];
      setOrders(rows);
    } finally {
      setOrderListLoading(false);
    }
  };

  const applyStatusFilter = (nextStatuses) => {
    setStatusFilter(nextStatuses);
    if (!isRepairOrdersList) return;
    loadOrders(orderSearch, orderYear, nextStatuses).catch((error) => {
      setStatus(error.response?.data?.error || error.message || `Failed to load ${pluralLabel}.`);
    });
  };

  const toggleStatusFilter = (value) => {
    const updated = new Set(statusFilter);
    updated.has(value) ? updated.delete(value) : updated.add(value);
    applyStatusFilter(updated);
  };

  const selectAllStatuses = () => {
    applyStatusFilter(new Set(statusOptions));
  };

  const clearStatusFilter = () => {
    applyStatusFilter(new Set());
  };

  const loadCustomers = async () => {
    const response = await api.get('/customers');
    const rows = response.data || [];
    setCustomers(rows);
    return rows;
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [
          customerResponse,
          customerTypesResponse,
          serviceResponse,
          serviceGroupsResponse,
          orderResponse,
          locationsResponse,
        ] = await Promise.all([
          api.get('/customers'),
          api.get('/customers/types'),
          api.get('/repair-orders/services'),
          api.get('/services/groups'),
          api.get(listEndpoint, {
            params: isRepairOrdersList
              ? { year: CURRENT_YEAR, status: 'open' }
              : { year: ALL_YEARS_VALUE },
          }),
          canReadLocations ? api.get('/locations') : Promise.resolve({ data: [] }),
        ]);
        setCustomers(customerResponse.data || []);
        setCustomerTypes(customerTypesResponse.data || []);
        setServices(serviceResponse.data || []);
        setServiceGroups(serviceGroupsResponse.data || []);
        setOrders(orderResponse.data || []);
        setLocations(locationsResponse.data || []);
        setInitialDataLoaded(true);
      } catch (error) {
        setStatus(error.response?.data?.error || error.message || `Failed to load ${singularLabel.toLowerCase()} data.`);
        setOrderLoading(false);
      }
    };

    loadInitialData();
  }, [canReadLocations, isRepairOrdersList, listEndpoint, singularLabel]);

  const resolveCustomerFromSerialInfo = useCallback((info) => {
    const soldCustomerId = info.soldCustomerId ? String(info.soldCustomerId) : '';
    const soldCustomerName = info.soldCustomerName || '';
    const currentLocation = info.currentLocation || '';
    const matchedCustomer = customers.find((customer) => {
      const customerId = String(getCustomerId(customer));
      const customerLabel = normalizeMatchText(getCustomerLabel(customer));
      return (
        (soldCustomerId && customerId === soldCustomerId)
        || (soldCustomerName && customerLabel === normalizeMatchText(soldCustomerName))
        || (currentLocation && customerLabel === normalizeMatchText(currentLocation))
      );
    });

    if (matchedCustomer) return matchedCustomer;
    if (soldCustomerId || soldCustomerName) {
      return {
        id: soldCustomerId,
        name: soldCustomerName,
        full_name: soldCustomerName,
        customertypeid: info.soldCustomerTypeId || '',
        customerTypeId: info.soldCustomerTypeId || '',
        customerTypeName: info.soldCustomerTypeName || '',
      };
    }
    return null;
  }, [customers]);

  const applyCustomerFromSerialInfo = useCallback((info) => {
    if (!info?.found || isClosedOrder) return null;

    const customer = resolveCustomerFromSerialInfo(info);
    if (customer) {
      setForm((current) => ({
        ...current,
        customerId: getCustomerId(customer),
        customerName: getCustomerLabel(customer) || current.customerName,
      }));
    }
    return customer;
  }, [isClosedOrder, resolveCustomerFromSerialInfo]);

  const validateCustomerReturnSerial = useCallback(async (customerId, serial) => {
    const response = await api.post('/transfers/customer-returns/serial', {
      customerId,
      serial,
    });
    return response.data;
  }, []);

  const inspectSerial = async (serialValue = form.serial) => {
    const serial = String(serialValue || '').trim();
    if (!serial) {
      setSerialInfo(null);
      setCustomerReturnValidatedSerial('');
      lastSerialLookupRef.current = '';
      return;
    }

    if (isNoSerialBypass(serial)) {
      lastSerialLookupRef.current = serial;
      setCustomerReturnValidatedSerial('');
      if (customerReturnMode) {
        setForm((current) => ({ ...current, serial }));
      } else {
        clearOrderWorkspace(serial, { preserveCustomer: true });
      }
      setSerialInfo({
        serial,
        found: true,
        noSerial: true,
        sold: false,
        warrantyStatus: 'No serial number',
        withinWarranty: false,
        itemName: 'No serial number',
        itemDescription: 'Repair order without serial number',
        currentLocation: '',
      });
      return;
    }

    if (lastSerialLookupRef.current === serial) return;
    lastSerialLookupRef.current = serial;

    let loadedSerialInfo = false;
    try {
      if (ENABLE_REPAIR_OPEN_ORDER_LOOKUP && !customerReturnMode) {
        const openOrderResponse = await api.get('/repair-orders/open-by-serial', { params: { serial } });
        const openOrder = openOrderResponse.data;
        if (openOrder?.id && String(openOrder.id) !== String(form.id || '')) {
          clearOrderWorkspace();
          setStatus(`Loading ${singularLabel.toLowerCase()} #${openOrder.id}...`);
          await nextFrame();
          await loadOrderIntoForm(openOrder.id);
          return;
        }
      }

      const response = await api.get('/repair-orders/serial-info', { params: { serial } });
      if (customerReturnMode) {
        setForm((current) => ({ ...current, serial }));
      } else {
        clearOrderWorkspace(serial, { preserveCustomer: true });
      }
      setSerialInfo(response.data);
      loadedSerialInfo = true;
      const returnCustomer = customerReturnMode && !isCustomerReturnLocked
        ? applyCustomerFromSerialInfo(response.data)
        : null;
      if (customerReturnMode) {
        if (!response.data?.sold) {
          closeCustomerReturnLocationChoice();
          setStatus('Customer returns are only allowed for sold items.');
          return;
        }
        const returnCustomerId = isCustomerReturnLocked ? form.customerId : getCustomerId(returnCustomer);
        if (returnCustomerId) {
          await validateCustomerReturnSerial(returnCustomerId, serial);
          setCustomerReturnValidatedSerial(serial);
          setStatus('');
          if (isCustomerReturnLocked) {
            closeCustomerReturnLocationChoice();
            await queueCustomerReturnSerial({
              customerIdOverride: returnCustomerId,
              locationOverride: lockedCustomerReturnLocation,
              inspectedSerialInfoOverride: response.data,
              serialOverride: serial,
            });
          } else {
            promptCustomerReturnLocation(returnCustomer);
          }
        } else {
          setCustomerReturnValidatedSerial('');
          closeCustomerReturnLocationChoice();
          setStatus('Select a valid Customer.');
        }
      }
    } catch (error) {
      lastSerialLookupRef.current = '';
      setCustomerReturnValidatedSerial('');
      closeCustomerReturnLocationChoice();
      if (!loadedSerialInfo) setSerialInfo(null);
      setStatus(error.response?.data?.error || error.message || 'Failed to inspect serial number.');
    }
  };

  const refreshSerialPanelForOrder = async (order) => {
    const serial = String(order?.serial || '').trim();
    if (!serial) {
      lastSerialLookupRef.current = '';
      setSerialInfo(null);
      return;
    }

    if (isNoSerialBypass(serial)) {
      lastSerialLookupRef.current = serial;
      setSerialInfo({
        serial,
        found: true,
        noSerial: true,
        sold: false,
        saleDate: null,
        warrantyExpires: null,
        warrantyStatus: 'No serial number',
        withinWarranty: false,
        currentLocation: '',
        itemName: 'No serial number',
        itemDescription: 'Repair order without serial number',
      });
      return;
    }

    try {
      const response = await api.get('/repair-orders/serial-info', { params: { serial } });
      lastSerialLookupRef.current = serial;
      setSerialInfo(response.data);
    } catch (error) {
      lastSerialLookupRef.current = '';
      setSerialInfo({
        found: true,
        sold: Boolean(order.saleDate),
        saleDate: order.saleDate,
        warrantyExpires: order.warrantyExpires,
        warrantyStatus: order.warrantyStatus,
        withinWarranty: order.withinWarranty,
        currentLocation: order.currentLocation,
        itemName: order.itemName,
        itemDescription: order.itemDescription,
      });
      setStatus(error.response?.data?.error || error.message || 'Failed to refresh serial information.');
    }
  };

  const selectCustomer = (customer) => {
    if (customerReturnMode) {
      setCustomerReturnValidatedSerial('');
      closeCustomerReturnLocationChoice();
    }
    setForm((current) => ({
      ...current,
      customerId: getCustomerId(customer),
      customerName: getCustomerLabel(customer),
    }));
  };

  const queueCustomerReturnSerial = async ({
    customerIdOverride = null,
    locationOverride = '',
    inspectedSerialInfoOverride = null,
    serialOverride = '',
  } = {}) => {
    if (!customerReturnMode) return;
    let customerId = customerIdOverride || form.customerId;
    const serial = String(serialOverride || form.serial || '').trim();
    if (!serial) {
      setStatus('Enter a serial number.');
      return;
    }
    if (isNoSerialBypass(serial)) {
      setStatus('Customer returns are only allowed for sold serial-numbered items.');
      return;
    }

    let inspectedSerialInfo = inspectedSerialInfoOverride || serialInfo;
    if (
      !inspectedSerialInfo
      || normalizeMatchText(inspectedSerialInfo.serial) !== normalizeMatchText(serial)
    ) {
      try {
        const serialResponse = await api.get('/repair-orders/serial-info', { params: { serial } });
        inspectedSerialInfo = {
          ...serialResponse.data,
          serial: serialResponse.data?.serial || serial,
        };
        setSerialInfo(inspectedSerialInfo);
        const returnCustomer = customerId || isCustomerReturnLocked
          ? null
          : applyCustomerFromSerialInfo(inspectedSerialInfo);
        customerId = customerId || getCustomerId(returnCustomer);
      } catch (error) {
        setSerialInfo(null);
        setStatus(error.response?.data?.error || error.message || 'Failed to inspect serial number.');
        return;
      }
    }

    if (!customerId) {
      const returnCustomer = resolveCustomerFromSerialInfo(inspectedSerialInfo);
      if (returnCustomer) {
        customerId = getCustomerId(returnCustomer);
        applyCustomerFromSerialInfo(inspectedSerialInfo);
      }
    }

    if (!inspectedSerialInfo?.sold) {
      setStatus('Customer returns are only allowed for sold items.');
      return;
    }
    if (!customerId) {
      setStatus('Select a valid Customer.');
      return;
    }

    setLoading(true);
    setStatus('');
    try {
      const row = await validateCustomerReturnSerial(customerId, serial);
      setCustomerReturnValidatedSerial(serial);
      const queuedSerials = new Set(customerReturnQueue
        .filter((item) => !isCustomerReturnAdjustmentRow(item))
        .map((item) => String(item.serial || '').trim().toLowerCase()));
      if (queuedSerials.has(String(row.serial || serial).trim().toLowerCase())) {
        setStatus('This serial number is already in the return queue.');
        return;
      }
      const firstQueuedRow = customerReturnQueue[0];
      if (
        firstQueuedRow?.customerId
        && row.customerId
        && Number(row.customerId) !== Number(firstQueuedRow.customerId)
      ) {
        setStatus('Location does not match the return queue.');
        return;
      }
      const returnLocation = locationOverride
        || lockedCustomerReturnLocation
        || customerReturnLocation
        || getLocationName(defaultCustomerReturnLocation)
        || DEFAULT_CUSTOMER_RETURN_LOCATION;
      setCustomerReturnLocation(returnLocation);
      setCustomerReturnQueue((current) => {
        const nextItem = {
          ...row,
          returnLocation,
        };
        const existingAdjustmentIds = new Set(current
          .filter(isCustomerReturnAdjustmentRow)
          .map((item) => String(item.id || '')));
        const adjustmentRows = (row.invoiceAdjustments || [])
          .filter((adjustment) => !existingAdjustmentIds.has(String(adjustment.id || '')))
          .map((adjustment) => ({
            ...adjustment,
            id: adjustment.id || `${adjustment.type || 'adjustment'}-${adjustment.sourceDocNo}-${adjustment.item}`,
            rowType: 'invoice_adjustment',
            adjustmentType: adjustment.type || 'service',
            sourceDocNo: adjustment.sourceDocNo || row.sourceDocNo,
            serial: '',
            qty: 1,
            quantity: 1,
            price: Number(adjustment.amount || 0),
            returnLocation,
          }));
        return [...current, nextItem, ...adjustmentRows];
      });
      setForm((current) => ({ ...current, serial: '' }));
      setSerialInfo(null);
      setCustomerReturnValidatedSerial('');
      lastSerialLookupRef.current = '';
    } catch (error) {
      setCustomerReturnValidatedSerial('');
      setStatus(error.response?.data?.error || error.message || 'Failed to add serial number to return.');
    } finally {
      setLoading(false);
    }
  };

  const removeCustomerReturnSerial = (row) => {
    setCustomerReturnQueue((current) => {
      if (isCustomerReturnAdjustmentRow(row)) {
        const removeKey = customerReturnRowKey(row);
        return current.filter((item) => customerReturnRowKey(item) !== removeKey);
      }
      const removedSerial = String(row.serial || '').trim().toLowerCase();
      const sourceDocNo = String(row.sourceDocNo || '');
      const remainingItems = current.filter((item) => (
        !isCustomerReturnAdjustmentRow(item)
        && String(item.serial || '').trim().toLowerCase() !== removedSerial
      ));
      const keepSourceAdjustments = remainingItems.some((item) => String(item.sourceDocNo || '') === sourceDocNo);
      return current.filter((item) => {
        if (!isCustomerReturnAdjustmentRow(item)) {
          return String(item.serial || '').trim().toLowerCase() !== removedSerial;
        }
        return keepSourceAdjustments || String(item.sourceDocNo || '') !== sourceDocNo;
      });
    });
  };

  const cancelCustomerReturn = () => {
    setCustomerReturnQueue([]);
    setCustomerReturnMemo('');
    setCustomerReturnLocation('');
    setCustomerReturnValidatedSerial('');
    closeCustomerReturnLocationChoice();
    setStatus('');
  };

  const completeCustomerReturn = async () => {
    if (!form.customerId) {
      setStatus('Select a valid Customer.');
      return;
    }
    if (!canCreateCustomerReturn) {
      setStatus('You can only create customer returns for allowed customer types.');
      return;
    }
    if (customerReturnQueuedItemCount === 0) {
      setStatus('Add at least one serial number to the return queue.');
      return;
    }

    const destinationLocationName = lockedCustomerReturnLocation || getLocationName(defaultCustomerReturnLocation) || DEFAULT_CUSTOMER_RETURN_LOCATION;
    const destinationLocation = findUsableLocationByName(locations, destinationLocationName);
    const destinationLocationId = getLocationId(destinationLocation);
    if (!destinationLocationId && !destinationLocationName) {
      setStatus(`${destinationLocationName} return location was not found.`);
      return;
    }

    setLoading(true);
    setStatus('');
    try {
      const response = await api.post('/transfers/customer-returns/credit-memo', {
        customerId: form.customerId,
        locationId: destinationLocationId || null,
        locationName: destinationLocationName,
        serials: customerReturnQueue
          .filter((row) => !isCustomerReturnAdjustmentRow(row))
          .map((row) => row.serial),
        invoiceAdjustments: customerReturnQueue
          .filter(isCustomerReturnAdjustmentRow)
          .map((row) => ({
            id: row.id,
            sourceDocNo: row.sourceDocNo,
            type: row.adjustmentType,
          })),
        preparedBy,
        memo: customerReturnMemo.trim(),
      });
      const docNo = response.data?.docNo;
      setStatus(`Credit Memo #${docNo} created.`);
      setCustomerReturnQueue([]);
      setCustomerReturnMemo('');
      setCustomerReturnLocation('');
      setCustomerReturnValidatedSerial('');
      await loadOrders(orderSearch);
      if (docNo) await openListDocument(docNo);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || 'Failed to create credit memo.');
    } finally {
      setLoading(false);
    }
  };

  const closeRetailCustomerModal = () => {
    setRetailCustomerModalOpen(false);
    setRetailCustomerStatus('');
    setRetailDuplicatePhoneCustomers([]);
    setNewRetailCustomer({ companyname: '', phone: '', email: '' });
  };

  const createRetailCustomer = async (event) => {
    event.preventDefault();
    setRetailDuplicatePhoneCustomers([]);
    if (!canCreateRetailCustomer) {
      setRetailCustomerStatus('You do not have permission to add retail customers.');
      return;
    }
    const name = newRetailCustomer.companyname.trim();
    if (!name) {
      setRetailCustomerStatus('Customer is required.');
      return;
    }

    setRetailCustomerSaving(true);
    setRetailCustomerStatus('');
    try {
      const response = await api.post('/customers', {
        name,
        companyname: name,
        customertypeid: getRetailCustomerTypeId(customerTypes),
        phone: newRetailCustomer.phone.trim(),
        email: newRetailCustomer.email.trim(),
      });
      const rows = await loadCustomers();
      const createdId = response.data?.id ? String(response.data.id) : '';
      const createdCustomer = rows.find((customer) => String(getCustomerId(customer)) === createdId)
        || rows.find((customer) => normalizeMatchText(getCustomerLabel(customer)) === normalizeMatchText(name));
      if (createdCustomer) {
        selectCustomer(createdCustomer);
      } else {
        setForm((current) => ({ ...current, customerId: createdId, customerName: name }));
      }
      closeRetailCustomerModal();
      setStatus(`Customer ${name} added.`);
    } catch (error) {
      setRetailCustomerStatus(error.response?.data?.error || error.message || 'Failed to add customer.');
      if (error.response?.data?.code === 'DUPLICATE_CUSTOMER_PHONE') {
        setRetailDuplicatePhoneCustomers(error.response.data.customers || []);
      }
    } finally {
      setRetailCustomerSaving(false);
    }
  };

  const updateService = (index, field, value) => {
    if (isClosedOrder) return;
    setForm((current) => ({
      ...current,
      services: current.services.map((service, serviceIndex) => (
        serviceIndex === index ? { ...service, [field]: value } : service
      )),
    }));
  };

  const removeService = (index) => {
    if (isClosedOrder) return;
    setForm((current) => ({
      ...current,
      services: current.services.filter((_, serviceIndex) => serviceIndex !== index),
    }));
  };

  const addServiceFromOption = (service) => {
    if (isClosedOrder || !service) return;
    const nameKey = getServiceNameKey(service);
    setForm((current) => {
      if (!isCustomService(service)
        && current.services.some((currentService) => getServiceNameKey(currentService) === nameKey)) {
        return current;
      }

      return {
        ...current,
        services: [
          ...current.services,
          withAutomaticGroupAssignments({
            itemId: service.itemId || service.id,
            custom: Boolean(service.custom),
            name: service.name,
            description: '',
            price: service.defaultPrice ?? service.price ?? 0,
            defaultPriceType: service.defaultPriceType ?? service.priceType ?? 'amount',
            priceType: service.defaultPriceType ?? service.priceType ?? 'amount',
            subServices: service.subServices ?? service.sub_services ?? 0,
            subServiceNames: service.subServiceNames ?? service.sub_service_names ?? getServiceSubServiceNames(service),
            groupAssignments: [],
          }, serviceGroups),
        ],
      };
    });
    setServiceSearch('');
  };

  const resetForm = () => {
    setForm(emptyForm);
    setSerialInfo(null);
    setServiceModalOpen(false);
    setServiceModalTab('repairs');
    setModalServiceIds([]);
    setModalServiceValues({});
    setModalServiceBaseAmounts({});
    setModalCustomServiceCount(0);
    setServiceSearch('');
    setCustomerReturnQueue([]);
    setCustomerReturnMemo('');
    setCustomerReturnLocation('');
    setCustomerReturnValidatedSerial('');
    closeCustomerReturnLocationChoice();
    setPersistedOrderStatus('');
    setDeleteConfirm({ open: false, orderId: null });
    setStatus('');
  };

  const resetAfterClosedSave = () => {
    resetForm();
    setCustomerReturnQueue([]);
    setCustomerReturnMemo('');
    setCustomerReturnLocation('');
    setCustomerReturnValidatedSerial('');
    lastSerialLookupRef.current = '';
  };

  const clearOrderWorkspace = (serial = '', { preserveCustomer = false } = {}) => {
    setForm((current) => ({
      ...emptyForm,
      serial,
      ...(preserveCustomer
        ? {
          customerId: current.customerId,
          customerName: current.customerName,
        }
        : {}),
    }));
    setSerialInfo(null);
    setServiceModalOpen(false);
    setServiceModalTab('repairs');
    setModalServiceIds([]);
    setModalServiceValues({});
    setModalServiceBaseAmounts({});
    setModalCustomServiceCount(0);
    setServiceSearch('');
    setCustomerReturnQueue([]);
    setCustomerReturnValidatedSerial('');
    setCustomerReturnMemo('');
    setCustomerReturnLocation('');
    closeCustomerReturnLocationChoice();
    setPersistedOrderStatus('');
    setInvoiceModal({ invoice: null, loading: false, error: '' });
    setTransferDocumentModal({ transfer: null, loading: false, error: '' });
    setCreditMemoInvoiceEditor({ open: false, docNo: '', draft: null, saving: false, error: '' });
    setInvoiceEmailLoading(false);
    setLoading(false);
    setStatus('');
  };

  const openServiceModal = () => {
    if (isClosedOrder) return;
    setModalServiceIds(form.services.filter((service) => !isCustomService(service)).map(getServiceKey));
    setModalServiceValues(Object.fromEntries(form.services
      .filter((service) => !isCustomService(service))
      .map((service) => [getServiceKey(service), servicePriceInputValue(service)])));
    setModalServiceBaseAmounts(Object.fromEntries(form.services
      .filter((service) => !isCustomService(service) && isCreditCardFeeServiceName(service.name))
      .filter((service) => service.serviceBaseAmount !== null && service.serviceBaseAmount !== undefined)
      .map((service) => [getServiceKey(service), formatNumber(service.serviceBaseAmount)])));
    setModalCustomServiceCount(form.services.filter(isCustomService).length);
    setServiceModalTab('repairs');
    setServiceModalOpen(true);
  };

  const toggleModalService = (serviceId) => {
    if (isClosedOrder) return;
    const id = String(serviceId);
    const service = serviceOptions.find((option) => getServiceKey(option) === id);
    setModalServiceIds((current) => (
      current.includes(id)
        ? current.filter((currentId) => currentId !== id)
        : [...current, id]
    ));
    setModalServiceValues((current) => (
      modalServiceIds.includes(id)
        ? Object.fromEntries(Object.entries(current).filter(([key]) => key !== id))
        : { ...current, [id]: current[id] ?? servicePriceInputValue(service) }
    ));
    setModalServiceBaseAmounts((current) => (
      modalServiceIds.includes(id)
        ? Object.fromEntries(Object.entries(current).filter(([key]) => key !== id))
        : current
    ));
  };

  const setModalServiceValue = (serviceId, value) => {
    const id = String(serviceId);
    setModalServiceValues((current) => ({ ...current, [id]: value }));
  };

  const setModalServiceBaseAmount = (serviceId, value) => {
    const id = String(serviceId);
    setModalServiceBaseAmounts((current) => ({ ...current, [id]: value }));
  };

  const saveServiceSelection = () => {
    if (isClosedOrder) return;
    const selectedIds = new Set(modalServiceIds.map(String));
    const existingById = new Map(
      form.services
        .filter((service) => !isCustomService(service))
        .map((service) => [getServiceKey(service), service])
    );
    const existingCustomServices = form.services.filter(isCustomService);
    const selectedCustomServices = Array.from(
      { length: modalCustomServiceCount },
      (_, index) => withAutomaticGroupAssignments(existingCustomServices[index] || {
        itemId: null,
        custom: true,
        name: CUSTOM_SERVICE.name,
        description: '',
        price: CUSTOM_SERVICE.defaultPrice,
        defaultPriceType: CUSTOM_SERVICE.defaultPriceType,
        priceType: CUSTOM_SERVICE.priceType,
        subServices: 0,
        subServiceNames: [],
        groupAssignments: [],
      }, serviceGroups)
    );
    const selectedNames = new Set();
    const selectedServiceOptions = serviceOptions
      .filter((service) => !isCustomService(service))
      .filter((service) => selectedIds.has(getServiceKey(service)))
      .filter((service) => {
        const nameKey = getServiceNameKey(service);
        if (selectedNames.has(nameKey)) return false;
        selectedNames.add(nameKey);
        return true;
      });
    const missingOtherCcBaseAmount = selectedServiceOptions.some((service) => {
      if (!isCreditCardFeeServiceName(service.name)) return false;
      const existing = existingById.get(getServiceKey(service));
      const key = getServiceKey(service);
      const rawValue = modalServiceValues[key] ?? servicePriceInputValue(existing || service);
      const fallbackPriceType = existing?.priceType ?? service.defaultPriceType ?? service.priceType ?? 'amount';
      const priceType = String(rawValue).includes('%')
        ? 'percent'
        : (String(fallbackPriceType || '').toLowerCase() === 'percent' ? 'percent' : 'amount');
      if (priceType !== 'percent') return false;
      return parseOptionalMoneyInput(modalServiceBaseAmounts[getServiceKey(service)]) === null;
    });
    if (missingOtherCcBaseAmount) {
      setStatus('Enter the Other-CC amount before saving.');
      return;
    }
    const selectedServices = selectedServiceOptions.map((service) => {
        const key = getServiceKey(service);
        const existing = existingById.get(key);
        const isOtherCcFee = isCreditCardFeeServiceName(service.name);
        const rawValue = modalServiceValues[key] ?? servicePriceInputValue(existing || service);
        const price = parseServicePriceInput(
          rawValue,
          service.defaultPrice ?? service.price ?? existing?.price ?? 0
        );
        const fallbackPriceType = existing?.priceType ?? service.defaultPriceType ?? service.priceType ?? 'amount';
        const priceType = String(rawValue).includes('%')
          ? 'percent'
          : (String(fallbackPriceType || '').toLowerCase() === 'percent' ? 'percent' : 'amount');
        const rawBaseAmount = modalServiceBaseAmounts[key];
        const serviceBaseAmount = isOtherCcFee ? parseOptionalMoneyInput(rawBaseAmount) : null;
        return withAutomaticGroupAssignments({
          ...(existing || {
            itemId: service.itemId || service.id,
            custom: Boolean(service.custom),
            name: service.name,
            description: '',
            defaultPriceType: service.defaultPriceType ?? service.priceType ?? 'amount',
            priceType: service.defaultPriceType ?? service.priceType ?? 'amount',
            subServices: service.subServices ?? service.sub_services ?? 0,
            subServiceNames: service.subServiceNames ?? service.sub_service_names ?? getServiceSubServiceNames(service),
            groupAssignments: [],
          }),
          price,
          serviceBaseAmount,
          defaultPriceType: priceType,
          priceType,
        }, serviceGroups);
      });

    setForm((current) => ({
      ...current,
      services: [...selectedCustomServices, ...selectedServices],
    }));
    setServiceModalOpen(false);
    setModalServiceValues({});
    setModalServiceBaseAmounts({});
  };

  const assignServiceGroup = (serviceIndex, subService, groupId) => {
    if (isClosedOrder) return;
    setForm((current) => ({
      ...current,
      services: current.services.map((service, index) => {
        if (index !== serviceIndex) return service;
        if (isOtherService(service)) return { ...service, groupAssignments: [] };
        const group = serviceGroups.find((candidate) => String(getGroupId(candidate)) === String(groupId));
        const currentAssignments = getServiceGroupAssignments(service).filter((assignment) => (
          String(assignment.subService ?? '') !== String(subService ?? '')
        ));
        const nextAssignments = groupId
          ? [
            ...currentAssignments,
            {
              subService,
              groupId: Number(groupId),
              groupName: group?.name || '',
            },
          ]
          : currentAssignments;
        nextAssignments.sort((a, b) => String(a.subService || '').localeCompare(String(b.subService || '')));
        return {
          ...service,
          groupAssignments: nextAssignments,
        };
      }),
    }));
  };

  const saveOrder = async (event) => {
    event.preventDefault();
    if (customerReturnMode) {
      if (!canCreateOrder) {
        setStatus('You do not have permission to create customer returns.');
        return;
      }
      await queueCustomerReturnSerial();
      return;
    }
    if (form.id ? !canEditOrder : !canCreateOrder) {
      setStatus(`You do not have permission to ${form.id ? 'modify' : 'create'} ${singularLabel.toLowerCase()}s.`);
      return;
    }
    if (isClosedOrder) {
      if (form.status === 'Open') {
        await openClosedOrder();
        return;
      }
      setStatus(`Closed ${pluralLabel} cannot be modified.`);
      return;
    }
    if (form.status === 'Closed' && !await confirmIncompleteRepairs()) return;
    setLoading(true);
    setStatus('');
    try {
      const servicesWithGroups = form.services.map((service) => withAutomaticGroupAssignments(service, serviceGroups));
      const payload = {
        customerId: form.customerId,
        serial: form.serial,
        customerSoldTo: form.customerSoldTo,
        notes: form.notes,
        services: servicesWithGroups,
        status: form.status,
        createdBy: preparedBy,
        updatedBy: preparedBy,
      };
      const response = form.id
        ? await api.put(`/repair-orders/${form.id}`, payload)
        : await api.post('/repair-orders', payload);
      const saved = response.data;
      await loadOrders();
      if (String(saved.status || payload.status || '').toLowerCase() === 'closed') {
        resetAfterClosedSave();
        showTemporaryStatus(`${singularLabel} #${saved.id} closed.${autoEmailStatusText(saved.autoEmail)}`);
        await openRepairInvoice(saved.id);
        return;
      }
      setForm(emptyForm);
      setSerialInfo(null);
      setServiceModalOpen(false);
      setServiceModalTab('repairs');
      setModalServiceIds([]);
      setModalServiceValues({});
      setModalServiceBaseAmounts({});
      setModalCustomServiceCount(0);
      setServiceSearch('');
      setCustomerReturnQueue([]);
      setCustomerReturnMemo('');
      setCustomerReturnLocation('');
      setCustomerReturnValidatedSerial('');
      closeCustomerReturnLocationChoice();
      setPersistedOrderStatus('');
      lastSerialLookupRef.current = '';
      showTemporaryStatus(`${singularLabel} #${saved.id} saved.${autoEmailStatusText(saved.autoEmail)}`);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || `Failed to save ${singularLabel.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  };

  const changeServiceTaskStatus = async (service, taskRow, nextStatus) => {
    const componentId = service.serviceRoomId || service.id;
    if (!canEditOrder || isClosedOrder || !form.id || !componentId || serviceStatusSaving) return;
    const orderId = form.id;
    const task = taskRow.task;
    setServiceStatusSaving(true);
    setStatus('');
    try {
      const response = await api.patch(`/repair-orders/services/${encodeURIComponent(componentId)}/${task ? 'tasks/status' : 'status'}`, {
        status: nextStatus,
        ...(task ? { subService: task.subService, groupId: task.groupId, groupName: task.groupName } : {}),
      });
      const updated = response.data.services?.find((entry) => entry.serviceRoomId === response.data.updatedServiceRoomId);
      if (!updated) throw new Error('Status saved. Refresh the order to display the latest service details.');
      setForm(current => current.id !== orderId ? current : ({
        ...current,
        services: current.services.map(entry => (entry.serviceRoomId || entry.id) !== componentId ? entry : ({
          ...entry,
          serviceRoomId: updated.serviceRoomId,
          serviceStatus: updated.serviceStatus,
          serviceInProgressAt: updated.serviceInProgressAt,
          serviceCompletedAt: updated.serviceCompletedAt,
          worker: updated.worker,
          serviceWorker: updated.serviceWorker,
          tasks: updated.tasks,
          taskProgress: updated.taskProgress,
        })),
      }));
      setStatus(`${service.name || 'Service'} marked ${nextStatus}.`);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || 'Failed to update service status.');
    } finally {
      setServiceStatusSaving(false);
    }
  };

  const loadOrderIntoForm = async (orderId) => {
    setOrderLoading(true);
    setActiveTab('new');
    try {
      const response = await api.get(`/repair-orders/${orderId}`);
      const order = response.data;
      setForm({
        id: order.id,
        customerId: order.customerId || '',
        customerName: order.customerName || '',
        serial: order.serial || '',
        customerSoldTo: order.customerSoldTo || '',
        notes: order.notes || '',
        services: (order.services || []).map((service) => withAutomaticGroupAssignments(service, serviceGroups)),
        invoiceServices: order.invoiceServices || [],
        status: order.status || 'Open',
        closedAt: order.closedAt || order.closedDate || null,
        closedBy: order.closedBy || '',
      });
      setPersistedOrderStatus(order.status || 'Open');
      setServiceModalOpen(false);
      setServiceModalTab('repairs');
      setModalServiceIds([]);
      setModalServiceValues({});
      setModalServiceBaseAmounts({});
      setModalCustomServiceCount(0);
      await refreshSerialPanelForOrder(order);
      setActiveTab('new');
      setStatus(`Loaded ${singularLabel.toLowerCase()} #${order.id}.`);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || `Failed to load ${singularLabel.toLowerCase()}.`);
    } finally {
      setOrderLoading(false);
    }
  };

  // Use the current loader after supporting data is committed, and load the link only once.
  useEffect(() => {
    if (!initialDataLoaded || !isRepairOrdersList || linkedOrderLoadedRef.current) return;
    linkedOrderLoadedRef.current = true;
    const orderId = new URLSearchParams(window.location.search).get('orderId');
    if (orderId && /^\d+$/.test(orderId)) {
      loadOrderIntoForm(orderId);
    }
  });

  const openClosedOrder = async () => {
    if (!form.id || !isClosedOrder) return;
    if (!canEditOrder) {
      setStatus(`You do not have permission to open ${singularLabel.toLowerCase()}s.`);
      return;
    }
    setLoading(true);
    setStatus('');
    try {
      const response = await api.post(`/repair-orders/${form.id}/open`, { updatedBy: preparedBy });
      const order = response.data;
      setForm({
        id: order.id,
        customerId: order.customerId || '',
        customerName: order.customerName || '',
        serial: order.serial || '',
        customerSoldTo: order.customerSoldTo || '',
        notes: order.notes || '',
        services: (order.services || []).map((service) => withAutomaticGroupAssignments(service, serviceGroups)),
        invoiceServices: order.invoiceServices || [],
        status: order.status || 'Open',
        closedAt: order.closedAt || order.closedDate || null,
        closedBy: order.closedBy || '',
      });
      setPersistedOrderStatus(order.status || 'Open');
      await refreshSerialPanelForOrder(order);
      await loadOrders();
      setStatus(`${singularLabel} #${order.id} opened.`);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || `Failed to open ${singularLabel.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  };

  const changeOrderStatus = async (nextStatus) => {
    if (nextStatus === 'Delete') {
      if (!canDeleteOrder) {
        setStatus(`You do not have permission to delete ${singularLabel.toLowerCase()}s.`);
        return;
      }
      setDeleteConfirm({ open: true, orderId: form.id });
      return;
    }

    if (!form.id || nextStatus === persistedOrderStatus) {
      setForm((current) => ({ ...current, status: nextStatus }));
      return;
    }

    if (nextStatus === 'Closed' && canEditOrder && !await confirmIncompleteRepairs()) return;
    setForm((current) => ({ ...current, status: nextStatus }));

    if (nextStatus === 'Open' && isClosedOrder) {
      await openClosedOrder();
      return;
    }

    if (nextStatus !== 'Closed' || persistedOrderStatus !== 'Open') return;
    if (!canEditOrder) {
      setForm((current) => ({ ...current, status: persistedOrderStatus || 'Open' }));
      setStatus(`You do not have permission to close ${singularLabel.toLowerCase()}s.`);
      return;
    }

    setLoading(true);
    setStatus('');
    try {
      const servicesWithGroups = form.services.map((service) => withAutomaticGroupAssignments(service, serviceGroups));
      const response = await api.put(`/repair-orders/${form.id}`, {
        customerId: form.customerId,
        serial: form.serial,
        customerSoldTo: form.customerSoldTo,
        notes: form.notes,
        services: servicesWithGroups,
        status: nextStatus,
      });
      const order = response.data;
      await loadOrders();
      resetAfterClosedSave();
      showTemporaryStatus(`${singularLabel} #${order.id} closed.${autoEmailStatusText(order.autoEmail)}`);
      await openRepairInvoice(order.id);
    } catch (error) {
      setForm((current) => ({ ...current, status: persistedOrderStatus || 'Open' }));
      setStatus(error.response?.data?.error || error.message || `Failed to close ${singularLabel.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  };

  const closeDeleteConfirm = () => {
    if (deleteLoading) return;
    setDeleteConfirm({ open: false, orderId: null });
  };

  const deleteRepairOrder = async () => {
    const orderId = deleteConfirm.orderId;
    if (!orderId) return;
    if (!canDeleteOrder) {
      setStatus(`You do not have permission to delete ${singularLabel.toLowerCase()}s.`);
      setDeleteConfirm({ open: false, orderId: null });
      return;
    }
    setDeleteLoading(true);
    setStatus('');
    try {
      await api.delete(`/repair-orders/${orderId}`);
      resetForm();
      lastSerialLookupRef.current = '';
      await loadOrders(orderSearch, orderYear);
      showTemporaryStatus(`${singularLabel} #${orderId} deleted.`);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || `Failed to delete ${singularLabel.toLowerCase()}.`);
      setDeleteConfirm({ open: false, orderId: null });
    } finally {
      setDeleteLoading(false);
    }
  };

  const searchOrders = async (event) => {
    event.preventDefault();
    const query = orderSearch;
    const year = orderYear;
    try {
      await loadOrders(query, year);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || `Failed to search ${pluralLabel}.`);
    }
  };

  const clearOrderSearch = async () => {
    setOrderSearch('');
    try {
      await loadOrders('', orderYear);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || `Failed to load ${pluralLabel}.`);
    }
  };

  const fetchSerialRows = async (serials) => {
    const serialList = [...new Set(serials.map((serial) => String(serial || '').trim()).filter(Boolean))];
    if (serialList.length === 0) return [];

    const responses = await Promise.all(
      serialList.map((serial) => api.get('/transfers/search/serial', {
        params: { serial, refresh: Date.now() },
      }))
    );
    const seen = new Set();
    return responses
      .flatMap((response) => response.data || [])
      .filter((row) => {
        const key = String(row.subLedgerId || `${row.serial}-${row.sourceDocNo}-${row.transactionTypeId}`);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const loadSerialResults = async (serials) => {
    const serialList = [...new Set(serials.map((serial) => String(serial || '').trim()).filter(Boolean))];
    if (serialList.length === 0) {
      setStatus('Enter a serial number.');
      return;
    }

    setSerialLoading(true);
    setStatus('');
    setSerialResults([]);

    try {
      const rows = await fetchSerialRows(serialList);
      setSerialResults(rows);
      if (rows.length === 0) setStatus('No serial history found for those serial numbers.');
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || 'Failed to search serial number.');
    } finally {
      setSerialLoading(false);
    }
  };

  const searchBySerial = async (event) => {
    event?.preventDefault();
    await loadSerialResults(parseSerialSearch(serialNo));
  };

  const closeInvoiceModal = () => {
    setInvoiceModal({ invoice: null, loading: false, error: '' });
    setRepairInvoicePrintView('invoice');
  };

  const closeRepairLog = () => {
    setRepairLogModal({ open: false, loading: false, error: '', events: [] });
  };

  const openRepairLog = async () => {
    if (!form.id) return;
    setRepairLogModal({ open: true, loading: true, error: '', events: [] });
    try {
      const response = await api.get(`/repair-orders/${form.id}/audit-log`);
      setRepairLogModal({ open: true, loading: false, error: '', events: response.data || [] });
    } catch (error) {
      setRepairLogModal({
        open: true,
        loading: false,
        error: error.response?.data?.error || error.message || 'Failed to load repair log.',
        events: [],
      });
    }
  };

  const closeTransferDocumentModal = () => {
    setTransferDocumentModal({ transfer: null, loading: false, error: '' });
    setCreditMemoInvoiceEditor({ open: false, docNo: '', draft: null, saving: false, error: '' });
  };

  const cancelCreditMemoInvoiceEditor = () => {
    setCreditMemoInvoiceEditor({ open: false, docNo: '', draft: null, saving: false, error: '' });
  };

  const openCreditMemoInvoiceEditor = () => {
    const document = transferToEmailInvoice(transferDocumentModal.transfer);
    if (!document || Number(document.transactionTypeId) !== 10) return;
    const draft = shippingInvoiceEditDraftFromDocument(document, 0);
    const docNo = draft.docNo || document.docNo || document.invoiceNo || transferDocumentModal.transfer?.docNo;
    if (!docNo) return;
    setCreditMemoInvoiceEditor({ open: true, docNo: String(docNo), draft, saving: false, error: '' });
  };

  const updateCreditMemoInvoiceDraftItem = (lineKey, field, value) => {
    setCreditMemoInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft
        ? {
          ...current.draft,
          items: current.draft.items.map((item) => (
            item.lineKey === lineKey ? { ...item, [field]: value } : item
          )),
        }
        : current.draft,
    }));
  };

  const updateCreditMemoInvoiceDraftMemo = (memo) => {
    setCreditMemoInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? { ...current.draft, memo } : current.draft,
    }));
  };

  const updateCreditMemoInvoiceDraftTaxRate = (taxRate) => {
    setCreditMemoInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? { ...current.draft, taxRate } : current.draft,
    }));
  };

  const saveCreditMemoInvoiceEditor = async () => {
    const docNo = creditMemoInvoiceEditor.docNo;
    if (!docNo || !creditMemoInvoiceEditor.draft) return;
    setCreditMemoInvoiceEditor((current) => ({ ...current, saving: true, error: '' }));
    setStatus('');
    try {
      const response = await api.patch(`/invoices/${encodeURIComponent(docNo)}`, {
        transactionTypeId: 10,
        memo: creditMemoInvoiceEditor.draft.memo,
      });
      setTransferDocumentModal({ transfer: response.data, loading: false, error: '' });
      cancelCreditMemoInvoiceEditor();
      setStatus(`Credit Memo #${docNo} saved.`);
    } catch (error) {
      setCreditMemoInvoiceEditor((current) => ({
        ...current,
        saving: false,
        error: error.response?.data?.error
          || error.response?.data?.message
          || (error.response?.status ? `Failed to save credit memo. Server returned ${error.response.status}.` : '')
          || error.message
          || 'Failed to save credit memo.',
      }));
    }
  };

  const printRepairBarcode = (invoice = null) => {
    const invoiceBarcode = Array.isArray(invoice?.items) ? getRepairInvoiceBarcode(invoice) : null;
    const serial = invoiceBarcode?.serial || String(serialInfo?.serial || form.serial || '').trim();
    const itemDescription = String(
      invoiceBarcode?.description
      || serialInfo?.itemDescription
      || serialInfo?.itemName
      || form.services?.[0]?.description
      || form.services?.[0]?.name
      || ''
    ).trim();

    try {
      const barcodeSvg = createCode128Svg(serial);
      const iframe = document.createElement('iframe');
      iframe.title = `Barcode ${serial}`;
      iframe.style.position = 'fixed';
      iframe.style.left = '-10000px';
      iframe.style.top = '0';
      iframe.style.width = '320px';
      iframe.style.height = '170px';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      const cleanup = () => {
        setTimeout(() => iframe.remove(), 250);
      };
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(`<!doctype html>
        <html>
          <head>
            <title>Barcode ${escapeHtml(serial)}</title>
            <style>
              @page { size: 2.25in 1.25in; margin: 0; }
              * { box-sizing: border-box; }
              html,
              body {
                background: #fff;
                color: #111;
                font-family: Arial, sans-serif;
                margin: 0;
                overflow: hidden;
                padding: 0;
                width: 2.25in;
                height: 1.25in;
              }
              .repair-barcode-label {
                align-items: center;
                display: flex;
                flex-direction: column;
                height: 1.25in;
                justify-content: center;
                overflow: hidden;
                padding: 0.06in 0.08in;
                text-align: center;
                width: 2.25in;
              }
              .repair-barcode-description {
                font-size: 11pt;
                font-weight: 800;
                letter-spacing: 0;
                line-height: 0.95;
                margin-bottom: 0.08in;
                max-height: 0.28in;
                overflow: hidden;
                text-transform: uppercase;
                width: 100%;
              }
              .repair-barcode-svg {
                display: block;
                flex: 0 0 auto;
                height: 0.42in;
                max-width: 1.9in;
                width: 1.9in;
              }
              .repair-barcode-serial {
                font-size: 19pt;
                font-weight: 500;
                line-height: 1;
                margin-top: 0.02in;
              }
            </style>
          </head>
          <body>
            <section class="repair-barcode-label">
              <div class="repair-barcode-description">${escapeHtml(itemDescription || 'Repair Order')}</div>
              ${barcodeSvg}
              <div class="repair-barcode-serial">${escapeHtml(serial)}</div>
            </section>
          </body>
        </html>`);
      doc.close();
      const runPrint = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        cleanup();
      };
      setTimeout(runPrint, 100);
    } catch (error) {
      setStatus(error.message || 'Failed to print barcode.');
    }
  };

  const openListDocument = async (docNo) => {
    if (!docNo) return;

    if (!listDocumentTransactionTypeId) {
      await openRepairInvoice(docNo);
      return;
    }

    setTransferDocumentModal({ transfer: null, loading: true, error: '' });
    try {
      const response = await api.get(`/transfers/${docNo}`, {
        params: { transactionTypeId: listDocumentTransactionTypeId },
      });
      setTransferDocumentModal({ transfer: response.data, loading: false, error: '' });
    } catch (error) {
      setTransferDocumentModal({
        transfer: null,
        loading: false,
        error: error.response?.data?.error || error.message || `Failed to load ${singularLabel.toLowerCase()}.`,
      });
    }
  };

  const printTransferDocument = () => {
    const source = document.querySelector('.repair-transfer-document-modal .shipdoc-invoice-workspace');
    if (!source) return;

    const printNode = source.cloneNode(true);
    printNode.querySelectorAll('.shipdoc-invoice-actions, .transfer-print-button').forEach((node) => node.remove());

    const iframe = document.createElement('iframe');
    iframe.title = 'Credit memo print preview';
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '850px';
    iframe.style.height = '1100px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 250);
    };

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    const headMarkup = Array.from(document.head.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((node) => node.outerHTML)
      .join('\n');

    doc.open();
    doc.write(`<!doctype html>
      <html>
        <head>
          <title>Credit Memo ${transferDocumentModal.transfer?.docNo || ''}</title>
          ${headMarkup}
          <style>
            @page { size: portrait; margin: 12mm; }
            body { background: #fff; margin: 0; }
            .shipdoc-invoice-workspace { background: #fff; padding: 0; }
            .shipdoc-invoice-sheet { box-shadow: none; margin: 0 auto; }
          </style>
        </head>
        <body>${printNode.outerHTML}</body>
      </html>`);
    doc.close();

    const runPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    };
    setTimeout(runPrint, 100);
  };

  const openRepairInvoice = async (orderId) => {
    if (!orderId) return;
    setRepairInvoicePrintView('invoice');
    setInvoiceModal({ invoice: null, loading: true, error: '' });
    try {
      const response = await api.get(`/invoices/RO-${orderId}`);
      setInvoiceModal({ invoice: response.data, loading: false, error: '' });
    } catch (error) {
      setInvoiceModal({
        invoice: null,
        loading: false,
        error: error.response?.data?.error || error.message || 'Failed to load invoice.',
      });
    }
  };

  const openSerialHistoryDocument = async (row) => {
    const sourceDocNo = row?.sourceDocNo || row?.docNo || row?.id;
    const transactionTypeId = Number(row?.transactionTypeId) || null;
    if (!sourceDocNo) return;

    if (transactionTypeId === REPAIR_ORDER_TRANSACTION_TYPE_ID) {
      await loadOrderIntoForm(sourceDocNo);
      return;
    }

    if (transactionTypeId === 5 || transactionTypeId === 10) {
      setTransferDocumentModal({ transfer: null, loading: true, error: '' });
      try {
        const response = await api.get(`/transfers/${sourceDocNo}`, {
          params: { transactionTypeId },
        });
        setTransferDocumentModal({ transfer: response.data, loading: false, error: '' });
      } catch (error) {
        setTransferDocumentModal({
          transfer: null,
          loading: false,
          error: error.response?.data?.error || error.message || 'Failed to load transfer.',
        });
      }
      return;
    }

    setRepairInvoicePrintView('invoice');
    setInvoiceModal({ invoice: null, loading: true, error: '' });
    try {
      const response = await api.get(`/invoices/${sourceDocNo}`);
      setInvoiceModal({ invoice: response.data, loading: false, error: '' });
    } catch (error) {
      setInvoiceModal({
        invoice: null,
        loading: false,
        error: error.response?.data?.error || error.message || 'Failed to load invoice.',
      });
    }
  };

  const saveAndOpenRepairInvoice = async () => {
    if (!canCreateOrder) {
      setStatus(`You do not have permission to create ${singularLabel.toLowerCase()}s.`);
      return;
    }
    if (form.id) {
      await openRepairInvoice(form.id);
      return;
    }
    if (form.services.length === 0) return;

    if (form.status === 'Closed' && !await confirmIncompleteRepairs()) return;
    setLoading(true);
    setStatus('');
    try {
      const servicesWithGroups = form.services.map((service) => withAutomaticGroupAssignments(service, serviceGroups));
      const response = await api.post('/repair-orders', {
        customerId: form.customerId,
        serial: form.serial,
        customerSoldTo: form.customerSoldTo,
        notes: form.notes,
        services: servicesWithGroups,
        status: form.status,
        createdBy: preparedBy,
        updatedBy: preparedBy,
      });
      const saved = response.data;
      await loadOrders();
      await loadOrderIntoForm(saved.id);
      showTemporaryStatus(`${singularLabel} #${saved.id} saved.${autoEmailStatusText(saved.autoEmail)}`);
      await openRepairInvoice(saved.id);
    } catch (error) {
      setStatus(error.response?.data?.error || error.message || `Failed to save ${singularLabel.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  };

  const printRepairInvoiceOutput = () => {
    const invoice = invoiceModal.invoice;
    if (!invoice) return;
    if (repairInvoicePrintView === 'shippingLabel' || repairInvoicePrintView === 'pickupLabel') {
      printTransferLabel({
        location: invoice.to,
        pickup: repairInvoicePrintView === 'pickupLabel',
        services: true,
        docNo: invoice.invoiceNo,
      });
      return;
    }
    if (repairInvoicePrintView === 'barcode') {
      printRepairBarcode(invoice);
      return;
    }

    const printClassName = 'invoice-document-printing';
    const cleanupPrintMode = () => {
      document.body.classList.remove(printClassName);
      window.removeEventListener('afterprint', cleanupPrintMode);
    };
    document.body.classList.add(printClassName);
    window.addEventListener('afterprint', cleanupPrintMode, { once: true });
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => window.print());
    });
  };

  const visibleStatus = customerReturnMode && /^Loaded customer return #/i.test(status)
    ? ''
    : status;
  const transferDocumentInvoice = transferToEmailInvoice(transferDocumentModal.transfer);
  const transferDocumentIsCreditMemo = Number(transferDocumentInvoice?.transactionTypeId) === 10;
  const transferDocumentCreditMemoEditing = creditMemoInvoiceEditor.open
    && String(creditMemoInvoiceEditor.docNo) === String(transferDocumentInvoice?.docNo || transferDocumentInvoice?.invoiceNo || transferDocumentModal.transfer?.docNo || '');

  return (
    <>
      <TopNav />
      {repairCloseConfirm && <RepairCloseConfirm
        services={repairCloseConfirm.services}
        onConfirm={() => answerRepairClose(true)}
        onCancel={() => answerRepairClose(false)}
      />}
      <ConfirmModal
        open={leaveConfirm.open}
        message="Current Transaction will be lost. Leave the page?"
        onConfirm={leaveConfirm.onConfirm}
        onCancel={() => setLeaveConfirm({ open: false, onConfirm: () => {} })}
      />
      <main className="inventory-container transfers-page repair-page">
        <h2 className="inventory-title">{pageTitle}</h2>

        <section className="transfer-search-card">
          <div className="transfer-search-tabs" role="tablist" aria-label="Repair order views">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'new'}
              className={activeTab === 'new' ? 'active' : ''}
              onClick={() => changeActiveTab('new')}
              disabled={orderLoading}
            >
              {newOrderTabLabel}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'orders'}
              className={activeTab === 'orders' ? 'active' : ''}
              onClick={() => changeActiveTab('orders')}
              disabled={orderLoading}
            >
              {listTabLabel}
            </button>
            {!customerReturnMode && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'serial'}
                className={activeTab === 'serial' ? 'active' : ''}
                onClick={() => changeActiveTab('serial')}
                disabled={orderLoading}
              >
                Find by Serial Number
              </button>
            )}
          </div>

          {visibleStatus && <div className="transfer-status repair-status">{visibleStatus}</div>}

          <div className="repair-order-workspace" aria-busy={orderLoading}>
            {orderLoading && (
              <div className="repair-table-loading" role="status">
                <span aria-hidden="true"></span>
                <strong>Loading {singularLabel.toLowerCase()}...</strong>
              </div>
            )}
            <div inert={orderLoading} className={orderLoading ? 'repair-order-loading-content' : undefined}>
          {activeTab === 'new' ? (
            <div role="tabpanel">
              <form className="inventory-form transfer-toolbar transfer-new-toolbar repair-new-toolbar" onSubmit={saveOrder}>
                <input
                  value={form.serial}
                  onChange={(event) => {
                    const serial = event.target.value;
                    lastSerialLookupRef.current = '';
                    setSerialInfo(null);
                    if (customerReturnMode) {
                      setCustomerReturnValidatedSerial('');
                      closeCustomerReturnLocationChoice();
                    }
                    if (form.id) {
                      clearOrderWorkspace(serial);
                      return;
                    }
                    setForm((current) => ({ ...current, serial }));
                  }}
                  onBlur={() => inspectSerial()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      inspectSerial();
                    } else if (event.key === 'Tab') {
                      inspectSerial();
                    }
                  }}
                  placeholder="Serial #"
                  aria-label="Serial Number"
                  disabled={isClosedOrder}
                />
                <SearchSelect
                  label="Customer"
                  value={form.customerName}
                  onChange={(value) => {
                    setForm((current) => ({ ...current, customerName: value, customerId: '' }));
                    if (customerReturnMode) {
                      setCustomerReturnLocation('');
                      setCustomerReturnValidatedSerial('');
                      closeCustomerReturnLocationChoice();
                    }
                  }}
                  options={customers}
                  placeholder="Select customer"
                  getLabel={getCustomerLabel}
                  getSearchText={(customer) => [
                    getCustomerLabel(customer),
                    getCustomerPhone(customer),
                  ].filter(Boolean).join(' ')}
                  onSelect={selectCustomer}
                  renderOption={renderCustomerOption}
                  disabled={isClosedOrder || isCustomerReturnLocked || isCustomerReturnWaitingForValidation}
                />
                {!customerReturnMode && (
                  <button
                    type="button"
                    className="button repair-add-customer-button"
                    onClick={() => setRetailCustomerModalOpen(true)}
                    disabled={isClosedOrder || !canCreateRetailCustomer}
                    title="Add retail customer"
                    aria-label="Add retail customer"
                  >
                    +
                  </button>
                )}
                {SHOW_CUSTOMER_RETURN_LOCATION_DROPDOWN && customerReturnMode && (
                  <select
                    className="repair-return-location-select"
                    value={customerReturnLocation}
                    onChange={(event) => setCustomerReturnLocation(event.target.value)}
                    disabled={isClosedOrder || isCustomerReturnLocked || isSelectedCustomerRetailOrCod || customerReturnLocationOptions.length <= 1}
                    aria-label="Customer Return Location"
                  >
                    {customerReturnLocationOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  value={form.customerSoldTo}
                  onChange={(event) => setForm((current) => ({ ...current, customerSoldTo: event.target.value }))}
                  placeholder="Sold To"
                  aria-label="Customer Sold Item To"
                  disabled={isClosedOrder || isCustomerReturnWaitingForValidation}
                />
                {!customerReturnMode && (
                  <select
                    value={form.status}
                    onChange={(event) => changeOrderStatus(event.target.value)}
                    disabled={loading}
                    aria-label={`${singularLabel} Status`}
                  >
                    <option value="Open">Open</option>
                    {(!form.id || canEditOrder) && <option value="Closed">Close</option>}
                    {form.id && canDeleteOrder && <option value="Delete">Delete</option>}
                  </select>
                )}
                <button
                  type="button"
                  className="button"
                  onClick={resetForm}
                  title="This action resets the page without saving or deleting an order."
                  aria-label="Reset. This action resets the page without saving or deleting an order."
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="button blue"
                  disabled={
                    loading
                    || isCustomerReturnSerialKnownUnsold
                    || isCustomerReturnWaitingForValidation
                    || (isClosedOrder && form.status !== 'Open')
                    || (form.id ? !canEditOrder : !canCreateOrder)
                  }
                >
                  {customerReturnMode ? (loading ? 'Adding...' : 'Add') : (loading ? 'Saving...' : 'Save')}
                </button>
                {!customerReturnMode && (
                  <>
                    <button
                      type="button"
                      className="button blue"
                      onClick={saveAndOpenRepairInvoice}
                      disabled={loading || invoiceModal.loading || (!form.id && (!canCreateOrder || form.services.length === 0))}
                    >
                      {loading || invoiceModal.loading ? 'Loading...' : 'Print'}
                    </button>
                    <button
                      type="button"
                      className="button blue"
                      onClick={openRepairLog}
                      disabled={!form.id || repairLogModal.loading}
                    >
                      Repair Log
                    </button>
                    {form.id && (
                      <button
                        type="button"
                        className="button repair-order-refresh"
                        onClick={() => loadOrderIntoForm(form.id)}
                        disabled={loading || orderLoading}
                        title="Refresh repair order from saved data"
                        aria-label="Refresh repair order"
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M20 11a8.1 8.1 0 0 0-15.5-2M4 5v4h4m-4 4a8.1 8.1 0 0 0 15.5 2M20 19v-4h-4" />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </form>

              <section className="inventory-card transfer-results repair-serial-panel">
                {!customerReturnMode && (
                  <div className="transfer-results-header">
                    <h3>
                      {form.id ? `${singularLabel} #${form.id}` : `New ${singularLabel}`}
                    </h3>
                    <div className="repair-order-status-details">
                      <strong className={`repair-order-status ${statusClassName(form.status)}`}>{form.status}</strong>
                      {closedDateTime && (
                        <span>
                          Closed by {form.closedBy || '-'} on {closedDateTime.date} at {closedDateTime.time}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              {serialInfo ? (
                <div className="repair-serial-grid">
                  <div>
                    <span>Item</span>
                    <strong>{serialInfo.itemName || serialInfo.itemDescription || '-'}</strong>
                  </div>
                  <div>
                    <span>Sold Date</span>
                    <strong>{serialInfo.sold ? formatDate(serialInfo.saleDate) : 'Not sold'}</strong>
                  </div>
                  <div>
                    <span>Warranty</span>
                    <strong className={serialInfo.withinWarranty ? 'repair-good' : 'repair-muted'}>
                      {serialInfo.warrantyStatus || '-'}
                    </strong>
                  </div>
                  <div>
                    <span>Warranty Expires</span>
                    <strong>{formatDate(serialInfo.warrantyExpires) || '-'}</strong>
                  </div>
                  <div>
                    <span>Current Location</span>
                    <strong>{serialInfo.currentLocation || '-'}</strong>
                  </div>
                  <div>
                    <span>Customer</span>
                    {selectedCustomerDetails ? (
                      <strong className="repair-customer-summary">
                        <span>{selectedCustomerDetails.name || '-'}</span>
                        {selectedCustomerDetails.addressLines.map((line) => (
                          <span key={line}>{line}</span>
                        ))}
                        {selectedCustomerDetails.phone && <span>{selectedCustomerDetails.phone}</span>}
                      </strong>
                    ) : (
                      <strong>-</strong>
                    )}
                  </div>
                </div>
              ) : (
                <div className="repair-empty-state">Enter a serial number to check sale, warranty, and location.</div>
              )}
              </section>

              {customerReturnMode ? (
                <section className="inventory-card transfer-results repair-services-panel transfer-queue-card">
                  <div className="transfer-results-header">
                    <h3>Queued Customer Return Serial Numbers</h3>
                    <div className="transfer-results-actions">
                      <button
                        type="button"
                        className="button small blue"
                        onClick={completeCustomerReturn}
                        disabled={loading || customerReturnQueuedItemCount === 0 || !canCreateCustomerReturn}
                      >
                        {loading ? 'Creating...' : 'Create Credit Memo'}
                      </button>
                      <button
                        type="button"
                        className="button small transfer-confirm-cancel"
                        onClick={cancelCustomerReturn}
                        disabled={loading || customerReturnQueue.length === 0}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  <div className="inventory-table-wrapper transfer-results-table-wrap">
                    <table className="inventory-table">
                      <thead>
                        <tr>
                          <th>Line #</th>
                          <th>Serial #</th>
                          <th>Item</th>
                          <th>Price</th>
                          <th>Qty</th>
                          <th>Return Location</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {customerReturnQueue.map((row, index) => {
                          const isAdjustment = isCustomerReturnAdjustmentRow(row);
                          const qty = isAdjustment ? 0 : (Number(row.qty ?? row.quantity ?? 1) || 1);
                          return (
                            <tr key={customerReturnRowKey(row)} className={isAdjustment ? 'customer-return-adjustment-row' : ''}>
                              <td>{index + 1}</td>
                              <td>{isAdjustment ? `Invoice #${row.sourceDocNo || ''}` : row.serial}</td>
                              <td>{isAdjustment ? `${row.item || row.description || 'Adjustment'}${row.description && row.description !== row.item ? ` - ${row.description}` : ''}` : (row.item || row.description || '-')}</td>
                              <td>{money(row.price)}</td>
                              <td>{isAdjustment ? '' : formatNumber(qty)}</td>
                              <td>{isAdjustment ? '' : (row.returnLocation || lockedCustomerReturnLocation || '-')}</td>
                              <td>
                                <button
                                  type="button"
                                  className="button small red transfer-remove-row"
                                  onClick={() => removeCustomerReturnSerial(row)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {customerReturnQueue.length === 0 && (
                          <tr>
                            <td colSpan="7">No serial numbers queued.</td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3">Total</td>
                          <td className="transfer-queue-total-value">{money(customerReturnQueueTotalPrice)}</td>
                          <td className="transfer-queue-total-value">{formatNumber(customerReturnQueueTotalQty)}</td>
                          <td colSpan="2" className="transfer-queue-prepared-by">Prepared By: {preparedBy || '-'}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="transfer-queue-memo">
                    <label htmlFor="customer-return-memo">Memo</label>
                    <textarea
                      id="customer-return-memo"
                      value={customerReturnMemo}
                      onChange={(event) => setCustomerReturnMemo(event.target.value)}
                      rows="3"
                      placeholder="Memo for this credit memo"
                    />
                  </div>
                </section>
              ) : (
                <section className="inventory-card transfer-results repair-services-panel">
                  <div className="transfer-results-header">
                    <h3>Services</h3>
                </div>
                {!isClosedOrder && (
                  <div className="repair-add-service-row">
                    <SearchSelect
                      value={serviceSearch}
                      onChange={setServiceSearch}
                      options={availableServiceOptions}
                      placeholder="Search services"
                      label="Search services"
                      getLabel={(service) => service.name}
                      renderOption={(service) => (
                        <span>{service.name}</span>
                      )}
                      className="repair-service-search"
                      onSelect={addServiceFromOption}
                      disabled={availableServiceOptions.length === 0}
                    />
                    <button
                      type="button"
                      className="button blue repair-add-service-button"
                      onClick={openServiceModal}
                    >
                      Add Service
                    </button>
                  </div>
                )}

                <div className="repair-services-table-wrap">
                  <table className="inventory-table repair-services-table repair-order-services-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Description</th>
                        <th>Group</th>
                        <th>Status</th>
                        <th>Started Date</th>
                        <th>Completed Date</th>
                        <th>Worker</th>
                        <th>Price</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.services.map((service, index) => {
                        const isOther = isOtherService(service);
                        return (
                        <tr key={`${getServiceKey(service)}-${index}`}>
                          <td>
                            <div className="repair-service-name-cell">
                              <strong>{service.name}</strong>
                              {getServiceSubServiceNames(service).length > 0 && (
                                <span>{getServiceSubServiceNames(service).join(', ')}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <input
                              value={service.description || ''}
                              onChange={(event) => updateService(index, 'description', event.target.value)}
                              placeholder="Optional"
                              disabled={isClosedOrder}
                            />
                          </td>
                          <td>
                            {!isOther && (
                              <div className="repair-service-group-stack">
                                {getAssignableGroupRows(service).map((subService) => {
                                  const assignment = getServiceGroupAssignment(service, subService);
                                  const groupOptions = getGroupOptionsForService(serviceGroups, service, subService);
                                  const canEditGroup = !isClosedOrder && isManualGroupAssignmentService(service);
                                  return (
                                    <label
                                      key={subService || 'service'}
                                      className={`repair-service-group-assignment${subService ? '' : ' single'}`}
                                    >
                                      {subService && <span>{subService}</span>}
                                      <select
                                        value={assignment?.groupId || ''}
                                        onChange={(event) => assignServiceGroup(index, subService, event.target.value)}
                                        disabled={!canEditGroup || groupOptions.length === 0}
                                      >
                                        <option value="">No group</option>
                                        {groupOptions.map((group) => (
                                          <option key={getGroupId(group)} value={getGroupId(group)}>
                                            {group.name}
                                          </option>
                                        ))}
                                      </select>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                          <td>
                            {!isOther && (
                              <div className="repair-service-task-stack">
                                {getServiceTaskRows(service).map((taskRow) => (
                                  <select
                                    className={`service-room-status-select ${statusClassName(taskRow.status)}`}
                                    key={`${getServiceKey(service)}-${index}-status-${taskRow.key}`}
                                    value={taskRow.status}
                                    aria-label={`${service.name || 'Service'} ${taskRow.subService || ''} status`}
                                    onChange={event => changeServiceTaskStatus(service, taskRow, event.target.value)}
                                    disabled={!canEditOrder || isClosedOrder || !form.id || !(service.serviceRoomId || service.id) || loading || orderLoading || serviceStatusSaving}
                                  >
                                    {['Pending', 'In Progress', 'Completed'].map(option => <option key={option} value={option}>{option}</option>)}
                                  </select>
                                ))}
                              </div>
                            )}
                          </td>
                          <td>
                            {!isOther && (
                              <div className="repair-service-task-stack">
                                {getServiceTaskRows(service).map((taskRow) => (
                                  <span key={`${getServiceKey(service)}-${index}-started-${taskRow.key}`}>
                                    {formatDateTime(taskRow.inProgressAt) || '-'}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td>
                            {!isOther && (
                              <div className="repair-service-task-stack">
                                {getServiceTaskRows(service).map((taskRow) => (
                                  <span key={`${getServiceKey(service)}-${index}-completed-${taskRow.key}`}>
                                    {String(taskRow.status).toLowerCase() === 'completed'
                                      ? (formatDateTime(taskRow.completedAt) || '-')
                                      : '-'}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td>
                            {!isOther && (
                              <div className="repair-service-task-stack">
                                {getServiceTaskRows(service).map((taskRow) => (
                                  <span key={`${getServiceKey(service)}-${index}-worker-${taskRow.key}`}>
                                    {taskRow.worker || '-'}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="repair-price-input">
                              <span>{servicePriceType(service) === 'percent' ? '%' : '$'}</span>
                              <input
                                type="number"
                                step="0.01"
                                value={service.price}
                                onChange={(event) => updateService(index, 'price', event.target.value)}
                                disabled={isClosedOrder}
                              />
                            </div>
                          </td>
                          <td>
                            {!isClosedOrder && (
                              <button
                                type="button"
                                className="button small red"
                                onClick={() => removeService(index)}
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                      {invoiceServiceTotals.adjustments.map((service) => (
                        <tr key={`invoice-service-${service.id}`}>
                          <td><strong>{service.name}</strong></td>
                          <td>{service.description || 'Invoice service'}</td>
                          <td colSpan="5"></td>
                          <td>
                            {service.priceType === 'percent' && <span>{service.price}% = </span>}
                            {money(service.calculatedAmount)}
                          </td>
                          <td></td>
                        </tr>
                      ))}
                      {form.services.length === 0 && invoiceServiceTotals.adjustments.length === 0 && (
                        <tr>
                          <td colSpan="9">No services added.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <label className="repair-order-notes">
                    <textarea
                      value={form.notes || ''}
                      onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                      placeholder="Notes"
                      rows="3"
                      disabled={isClosedOrder}
                    />
                  </label>
                  <div className="repair-services-total">
                    <span>Total:</span>
                    <strong>{money(total)}</strong>
                  </div>
                </div>
                </section>
              )}
            </div>
          ) : activeTab === 'serial' ? (
            <div role="tabpanel">
              <form
                className="inventory-form transfer-toolbar transfer-serial-search-form repair-serial-search-form"
                onSubmit={searchBySerial}
              >
                <input
                  type="text"
                  placeholder="Serial #"
                  value={serialNo}
                  onChange={(event) => setSerialNo(event.target.value)}
                  autoFocus
                />
                <button type="submit" className="button small blue" disabled={serialLoading}>
                  {serialLoading ? 'Searching...' : 'Find'}
                </button>
              </form>

              {serialResults.length > 0 && (
                <section className="inventory-card transfer-results transfer-serial-results repair-serial-results">
                  <div className="transfer-results-header">
                    <h3>Serial Number Transaction History</h3>
                    {serialCurrentLocation && (
                      <div className="transfer-current-location">
                        <span>Current Location</span>
                        <strong>{serialCurrentLocation}</strong>
                      </div>
                    )}
                  </div>
                  <div className="inventory-table-wrapper transfer-results-table-wrap">
                    <table className="inventory-table transfer-serial-history-table">
                      <thead>
                        <tr>
                          <th>Doc No</th>
                          <th>Transaction Type</th>
                          <th>Date</th>
                          <th>Name</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {serialResults.map((row) => {
                          const openSerialDoc = () => openSerialHistoryDocument(row);
                          return (
                            <tr
                              key={row.subLedgerId || `${row.sourceDocNo}-${row.transactionTypeId}`}
                              className="transfer-clickable-row"
                              onClick={openSerialDoc}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  openSerialDoc();
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <td>
                                <button
                                  type="button"
                                  className="transfer-serial-link"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openSerialDoc();
                                  }}
                                >
                                  {row.sourceDocNo}
                                </button>
                              </td>
                              <td>{row.transactionType || row.transactionTypeId || ''}</td>
                              <td>{formatDate(row.sourceDate)}</td>
                              <td>
                                {Number(row.transactionTypeId) === 5
                                  ? (row.name || [row.shipFrom, row.shipTo].filter(Boolean).join('/') || row.location || '')
                                  : (row.shipTo || row.name || row.location || row.vendor || '')}
                              </td>
                              <td></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </div>
          ) : (
            <div role="tabpanel">
              <form
                className="inventory-form transfer-toolbar transfer-sales-search-row repair-search-row"
                onSubmit={searchOrders}
                aria-busy={orderListLoading}
              >
                <input
                  value={orderSearch}
                  onChange={(event) => setOrderSearch(event.target.value)}
                  placeholder="Search by customer, phone, serial number, or order number"
                />
                <button
                  type="button"
                  className="button small transfer-sales-clear-button"
                  onClick={clearOrderSearch}
                  disabled={!orderSearch.trim() || orderListLoading}
                >
                  Clear
                </button>
                {(!isRepairOrdersList || statusFilter.size === 0 || statusFilter.has('Closed')) && (
                  <select
                    value={orderYear}
                    onChange={(event) => {
                      const year = event.target.value;
                      setOrderYear(year);
                      const query = orderSearch;
                      loadOrders(query, year).catch((error) => {
                        setStatus(error.response?.data?.error || error.message || `Failed to load ${pluralLabel}.`);
                      });
                    }}
                    aria-label={`${listTabLabel} Year`}
                  >
                    {!isRepairOrdersList && <option value={ALL_YEARS_VALUE}>All</option>}
                    {YEAR_OPTIONS.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                )}
              </form>

              <div className={`inventory-card transfer-results repair-orders-table-wrap ${orderListLoading ? 'loading' : ''}`}>
              {orderListLoading && (
                <div className="repair-table-loading">
                  <span></span>
                  <strong>Loading results...</strong>
                </div>
              )}
              <table className={`inventory-table repair-orders-table ${allowListSelection ? 'repair-orders-selectable-table' : 'repair-document-list-table'}`}>
                <thead>
                  <tr>
                    <th>{listPrimaryColumnLabel}</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Serial</th>
                    <th
                      className="filter-header repair-status-filter-header"
                      onClick={() => setOpenStatusFilter((current) => !current)}
                      onKeyDown={(event) => {
                        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault();
                          setOpenStatusFilter((current) => !current);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="inventory-filter-trigger">
                        <span>Status</span>
                        <button
                          type="button"
                          className="filter-icon-button"
                          aria-label={`Filter ${singularLabel.toLowerCase()} status`}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" className="filter-icon">
                            <path d="M4 5h16l-6.5 7.5V18l-3 1v-6.5L4 5z" />
                          </svg>
                        </button>
                      </div>
                      {openStatusFilter && (
                        <div
                          className="filter-dropdown repair-status-filter-dropdown"
                          onClick={(event) => event.stopPropagation()}
                          onMouseLeave={() => setOpenStatusFilter(false)}
                        >
                          <div className="filter-actions">
                            <button type="button" onClick={selectAllStatuses}>Select All</button>
                            <button type="button" onClick={clearStatusFilter}>Clear</button>
                          </div>
                          <div className="filter-options">
                            {statusOptions.map((value) => (
                              <label key={value} className="filter-option">
                                <input
                                  type="checkbox"
                                  checked={statusFilter.has(value)}
                                  onChange={() => toggleStatusFilter(value)}
                                />
                                <span className="filter-value">{value}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </th>
                    <th>{listWarrantyColumnLabel}</th>
                    <th>Total</th>
                    {allowListSelection && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr
                      key={order.id}
                      className="repair-order-row"
                      onClick={() => {
                        if (allowListSelection) {
                          loadOrderIntoForm(order.id);
                        } else {
                          openListDocument(order.docNo || order.id);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          if (allowListSelection) {
                            loadOrderIntoForm(order.id);
                          } else {
                            openListDocument(order.docNo || order.id);
                          }
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <td>
                        {allowListSelection ? (
                          <strong>{order.id}</strong>
                        ) : (
                          <button
                            type="button"
                            className="repair-ro-link"
                            onClick={(event) => {
                              event.stopPropagation();
                              openListDocument(order.docNo || order.id);
                            }}
                          >
                            {order.docNo || order.id}
                          </button>
                        )}
                      </td>
                      <td>
                        {renderTransactionTimestamp(
                          customerReturnMode
                            ? (order.orderDate || order.dateCreated)
                            : (order.dateCreated || order.orderDate)
                        )}
                      </td>
                      <td>{order.customerName}</td>
                      <td>{order.serial}</td>
                      <td>
                        <div className="repair-list-status-cell">
                          <span>{order.status}</span>
                          {String(order.status || '').trim().toLowerCase() === 'closed' && order.closedAt && (
                            <small>{formatTransactionTimestampText(order.closedAt)}</small>
                          )}
                        </div>
                      </td>
                      <td>{order.warrantyStatus || '-'}</td>
                      <td>{money(order.total)}</td>
                      {allowListSelection && (
                        <td>
                          <button
                            type="button"
                            className="button small blue repair-invoice-list-button"
                            disabled={String(order.status || '').trim().toLowerCase() !== 'closed'}
                            title={String(order.status || '').trim().toLowerCase() === 'closed' ? 'Open invoice' : 'Invoice is available after the order is closed'}
                            onClick={(event) => {
                              event.stopPropagation();
                              openRepairInvoice(order.id);
                            }}
                          >
                            Invoice
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {!orderListLoading && filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={allowListSelection ? 8 : 7}>
                        {orders.length > 0 && statusFilter.size > 0
                          ? `No ${pluralLabel} match the selected status filter.`
                          : `No ${pluralLabel} found.`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </div>
          )}
            </div>
          </div>
        </section>
        {repairLogModal.open && (
          <div className="repair-log-overlay" onClick={closeRepairLog}>
            <section className="repair-log-modal" onClick={(event) => event.stopPropagation()}>
              <header className="repair-log-header">
                <h3>Repair Log - {singularLabel} #{form.id}</h3>
                <button type="button" className="button small red" onClick={closeRepairLog}>Close</button>
              </header>
              {repairLogModal.loading && <p className="repair-log-message">Loading repair log...</p>}
              {!repairLogModal.loading && repairLogModal.error && (
                <p className="repair-log-message error">{repairLogModal.error}</p>
              )}
              {!repairLogModal.loading && !repairLogModal.error && repairLogModal.events.length === 0 && (
                <p className="repair-log-message">No repair activity was found.</p>
              )}
              {!repairLogModal.loading && !repairLogModal.error && repairLogModal.events.length > 0 && (
                <div className="repair-log-table-wrap">
                  <table className="inventory-table repair-log-table">
                    <thead>
                      <tr>
                        <th>Date &amp; Time</th>
                        <th>Activity</th>
                        <th>Performed By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repairLogModal.events.map((event, index) => (
                        <tr key={event.id || `${event.eventType}-${event.eventAt}-${index}`}>
                          <td>{formatDateTime(event.eventAt) || '-'}</td>
                          <td>{event.description || event.eventType || '-'}</td>
                          <td>{event.actor || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
        {deleteConfirm.open && (
          <div className="transfer-confirm-overlay" onClick={closeDeleteConfirm}>
            <div className="transfer-confirm-modal" onClick={(event) => event.stopPropagation()}>
              <h3>Delete Repair Order?</h3>
              <p>
                Are you sure you want to delete <strong>Repair Order #{deleteConfirm.orderId}</strong>?
              </p>
              <div className="transfer-confirm-actions">
                <button
                  type="button"
                  className="button small transfer-confirm-cancel"
                  onClick={closeDeleteConfirm}
                  disabled={deleteLoading}
                >
                  No
                </button>
                <button
                  type="button"
                  className="button small red"
                  onClick={deleteRepairOrder}
                  disabled={deleteLoading}
                  autoFocus
                >
                  {deleteLoading ? 'Deleting...' : 'Yes'}
                </button>
              </div>
            </div>
          </div>
        )}
        {customerReturnLocationChoice.open && (
          <div className="transfer-confirm-overlay" onClick={closeCustomerReturnLocationChoice}>
            <div className="transfer-confirm-modal" onClick={(event) => event.stopPropagation()}>
              <button
                type="button"
                className="transfer-modal-close"
                onClick={closeCustomerReturnLocationChoice}
              >
                x
              </button>
              <h3>Return Location</h3>
              <p>
                Return items for <strong>{getCustomerLabel(customerReturnLocationChoice.customer)}</strong> to which location?
              </p>
              <div className="transfer-confirm-actions">
                <button
                  type="button"
                  className="button small transfer-confirm-cancel"
                  onClick={() => chooseCustomerReturnLocation(customerReturnLocationChoice.customerLocationName, { queueCurrentSerial: true })}
                  disabled={!customerReturnLocationChoice.customerLocationName}
                >
                  {customerReturnLocationChoice.customerLocationName || 'No location found'}
                </button>
                <button
                  type="button"
                  className="button small blue"
                  onClick={() => chooseCustomerReturnLocation(getLocationName(defaultCustomerReturnLocation) || DEFAULT_CUSTOMER_RETURN_LOCATION, { queueCurrentSerial: true })}
                >
                  Irenes Wigs - New York
                </button>
              </div>
            </div>
          </div>
        )}
        {retailCustomerModalOpen && (
          <div className="transfer-confirm-overlay" onClick={closeRetailCustomerModal}>
            <form
              className="transfer-confirm-modal repair-customer-add-modal"
              onSubmit={createRetailCustomer}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="transfer-modal-close"
                onClick={closeRetailCustomerModal}
                disabled={retailCustomerSaving}
              >
                x
              </button>
              <h3>Add Retail Customer</h3>
              <label>
                <span>Customer</span>
                <input
                  className="transfer-confirm-input"
                  type="text"
                  value={newRetailCustomer.companyname}
                  onChange={(event) => setNewRetailCustomer((current) => ({
                    ...current,
                    companyname: event.target.value,
                  }))}
                  autoFocus
                  required
                />
              </label>
              <label>
                <span>Phone</span>
                <input
                  className="transfer-confirm-input"
                  type="tel"
                  value={newRetailCustomer.phone}
                  onChange={(event) => {
                    setNewRetailCustomer((current) => ({ ...current, phone: event.target.value }));
                    setRetailDuplicatePhoneCustomers([]);
                    setRetailCustomerStatus('');
                  }}
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  className="transfer-confirm-input"
                  type="email"
                  value={newRetailCustomer.email}
                  onChange={(event) => setNewRetailCustomer((current) => ({
                    ...current,
                    email: event.target.value,
                  }))}
                />
              </label>
              {retailCustomerStatus && <p className="transfer-confirm-error">{retailCustomerStatus}</p>}
              {retailDuplicatePhoneCustomers.length > 0 && (
                <div className="customer-phone-matches" role="alert">
                  <strong>Customers with this phone number</strong>
                  <ul>
                    {retailDuplicatePhoneCustomers.map((customer) => (
                      <li key={customer.id}>
                        <strong>{customer.name}</strong> (#{customer.id})
                        <div>Phone: {customer.phone || '—'}</div>
                        {customer.altphone && <div>Alternate: {customer.altphone}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="transfer-confirm-actions">
                <button
                  type="button"
                  className="button small transfer-confirm-cancel"
                  onClick={closeRetailCustomerModal}
                  disabled={retailCustomerSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="button small blue" disabled={retailCustomerSaving || !canCreateRetailCustomer}>
                  {retailCustomerSaving ? 'Adding...' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        )}
        {serviceModalOpen && !isClosedOrder && (
          <div className="repair-service-modal-overlay" onClick={() => setServiceModalOpen(false)}>
            <div className="repair-service-modal" onClick={(event) => event.stopPropagation()}>
              <div className="repair-service-modal-header">
                <h3>Select Services</h3>
                <strong>{modalServiceIds.length + modalCustomServiceCount} selected</strong>
              </div>

              <div className="repair-service-modal-tabs" role="tablist" aria-label="Repair service categories">
                <button
                  type="button"
                  role="tab"
                  aria-selected={serviceModalTab === 'repairs'}
                  className={serviceModalTab === 'repairs' ? 'active' : ''}
                  onClick={() => setServiceModalTab('repairs')}
                >
                  Repairs
                  {selectedRepairServiceCount > 0 && <span>{selectedRepairServiceCount}</span>}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={serviceModalTab === 'other'}
                  className={serviceModalTab === 'other' ? 'active' : ''}
                  onClick={() => setServiceModalTab('other')}
                >
                  Other Services
                  {selectedOtherRepairServiceCount > 0 && <span>{selectedOtherRepairServiceCount}</span>}
                </button>
              </div>

              <div className="repair-service-modal-list">
                {[0, 1].map((columnIndex) => (
                  <div className="repair-service-column" key={columnIndex}>
                    {activeServiceModalOptions
                      .filter((_, serviceIndex) => serviceIndex % 2 === columnIndex)
                      .map((service) => {
                        const id = getServiceKey(service);
                        if (isCustomService(service)) {
                          return (
                            <div
                              key={id}
                              className="repair-service-option repair-custom-service-option"
                              role="button"
                              tabIndex="0"
                              aria-label="Add another Repair"
                              onClick={() => setModalCustomServiceCount((current) => current + 1)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  setModalCustomServiceCount((current) => current + 1);
                                }
                              }}
                            >
                              <span
                                className="repair-custom-service-add"
                                title="Add another Repair"
                                aria-hidden="true"
                              >
                                +
                              </span>
                              <div className="repair-custom-service-main">
                                <span className="repair-custom-service-label">
                                  {service.name}
                                  {modalCustomServiceCount > 0 && <small> x{modalCustomServiceCount}</small>}
                                </span>
                              </div>
                              <strong>{servicePriceLabel(service)}</strong>
                            </div>
                          );
                        }
                        if (serviceModalTab === 'other') {
                          const selectedService = modalServiceIds.includes(id);
                          const priceType = servicePriceType(service);
                          const valueSymbol = String(modalServiceValues[id] ?? servicePriceInputValue(service)).includes('%') ? '%' : priceType === 'percent' ? '%' : '$';
                          const isOtherCcFee = isCreditCardFeeServiceName(service.name);
                          return (
                            <div key={id} className={`repair-service-option repair-service-option-editable${isOtherCcFee ? ' repair-service-option-cc' : ''}`}>
                              <input
                                type="checkbox"
                                checked={selectedService}
                                onChange={() => toggleModalService(id)}
                              />
                              <span>{service.name}</span>
                              <label className="repair-service-value">
                                <small>{valueSymbol}</small>
                                <input
                                  type="text"
                                  disabled={!selectedService}
                                  value={modalServiceValues[id] ?? servicePriceInputValue(service)}
                                  onChange={(event) => setModalServiceValue(id, event.target.value)}
                                  aria-label={`${service.name} service value`}
                                />
                              </label>
                              {isOtherCcFee && (
                                <label className="repair-service-value repair-service-base">
                                  <small>$</small>
                                  <input
                                    type="text"
                                    disabled={!selectedService}
                                    value={modalServiceBaseAmounts[id] ?? ''}
                                    onChange={(event) => setModalServiceBaseAmount(id, event.target.value)}
                                    aria-label="Other-CC amount"
                                    placeholder="Amount"
                                  />
                                </label>
                              )}
                            </div>
                          );
                        }
                        return (
                          <label key={id} className="repair-service-option">
                            <input
                              type="checkbox"
                              checked={modalServiceIds.includes(id)}
                              onChange={() => toggleModalService(id)}
                            />
                            <span>{service.name}</span>
                            <strong>{servicePriceLabel(service)}</strong>
                          </label>
                        );
                      })}
                  </div>
                ))}
                {activeServiceModalOptions.length === 0 && (
                  <p className="repair-service-modal-empty">No services found.</p>
                )}
              </div>

              <div className="repair-service-modal-actions">
                <button type="button" className="button repair-service-cancel" onClick={() => setServiceModalOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="button blue" onClick={saveServiceSelection}>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        {(invoiceModal.invoice || invoiceModal.loading || invoiceModal.error) && (
          <div className="invoice-modal-overlay print-modal-overlay" onClick={closeInvoiceModal}>
            <div className="invoice-modal print-modal" onClick={(event) => event.stopPropagation()}>
              <div className="invoice-modal-header">
                <div className="transfer-opened-print-heading">
                  <h3>
                    {invoiceModal.invoice
                      ? `Invoice #${invoiceModal.invoice.invoiceNo}`
                      : 'Invoice'}
                  </h3>
                  {invoiceModal.invoice && (
                    <div className="transfer-output-tabs" role="tablist" aria-label="Repair invoice print documents">
                      {[
                        ['invoice', 'Invoice'],
                        ['shippingLabel', 'Delivery Label'],
                        ['pickupLabel', 'Pick Up Label'],
                        ['barcode', 'Bar Code'],
                      ].map(([view, label]) => (
                        <button
                          key={view}
                          type="button"
                          role="tab"
                          aria-selected={repairInvoicePrintView === view}
                          className={repairInvoicePrintView === view ? 'active' : ''}
                          onClick={() => setRepairInvoicePrintView(view)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="invoice-modal-actions">
                  <InvoicePrintEmailActions
                    invoice={invoiceModal.invoice}
                    disabled={!invoiceModal.invoice}
                    loading={invoiceEmailLoading}
                    onLoadingChange={setInvoiceEmailLoading}
                    onPrint={printRepairInvoiceOutput}
                    printLabel={`Print ${repairInvoicePrintView === 'invoice'
                      ? 'Invoice'
                      : repairInvoicePrintView === 'shippingLabel'
                        ? 'Delivery Label'
                        : repairInvoicePrintView === 'pickupLabel'
                          ? 'Pick Up Label'
                          : 'Bar Code'}`}
                    successMessage={(to) => `Invoice #${invoiceModal.invoice.invoiceNo} emailed to ${to}.`}
                    onError={setStatus}
                  />
                  <button type="button" className="button small red" onClick={closeInvoiceModal}>
                    Close
                  </button>
                </div>
              </div>
              {invoiceModal.loading && <p className="invoice-modal-status">Loading invoice...</p>}
              {!invoiceModal.loading && invoiceModal.error && (
                <p className="invoice-modal-status error">{invoiceModal.error}</p>
              )}
              {!invoiceModal.loading && invoiceModal.invoice && repairInvoicePrintView === 'invoice' && (
                <ShipDocInvoiceDocument invoice={invoiceModal.invoice} />
              )}
              {!invoiceModal.loading && invoiceModal.invoice && repairInvoicePrintView === 'shippingLabel' && (
                <div className="transfer-output-preview transfer-opened-label-preview">
                  <TransferPrintLabel location={invoiceModal.invoice.to} services />
                </div>
              )}
              {!invoiceModal.loading && invoiceModal.invoice && repairInvoicePrintView === 'pickupLabel' && (
                <div className="transfer-output-preview transfer-opened-label-preview">
                  <TransferPrintLabel location={invoiceModal.invoice.to} pickup services />
                </div>
              )}
              {!invoiceModal.loading && invoiceModal.invoice && repairInvoicePrintView === 'barcode' && (
                <RepairBarcodePreview invoice={invoiceModal.invoice} />
              )}
            </div>
          </div>
        )}
        {(transferDocumentModal.transfer || transferDocumentModal.loading || transferDocumentModal.error) && (
          <div className="invoice-modal-overlay print-modal-overlay" onClick={closeTransferDocumentModal}>
            <div
              className="invoice-modal print-modal repair-transfer-document-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="invoice-modal-header">
                <h3>
                  {transferDocumentModal.transfer
                    ? `${transferDocumentModal.transfer.documentTitle || 'Credit Memo'} #${transferDocumentModal.transfer.docNo}`
                    : 'Credit Memo'}
                </h3>
                <div className="invoice-modal-actions">
                  <InvoicePrintEmailActions
                    invoice={transferDocumentInvoice}
                    disabled={!transferDocumentModal.transfer}
                    loading={invoiceEmailLoading}
                    onLoadingChange={setInvoiceEmailLoading}
                    onPrint={printTransferDocument}
                    successMessage={(to) => `${transferDocumentModal.transfer?.documentTitle || 'Credit Memo'} #${transferDocumentModal.transfer?.docNo} emailed to ${to}.`}
                    onError={setStatus}
                  />
                  {transferDocumentIsCreditMemo && (
                    <button
                      type="button"
                      className="button small yellow"
                      onClick={transferDocumentCreditMemoEditing ? saveCreditMemoInvoiceEditor : openCreditMemoInvoiceEditor}
                      disabled={transferDocumentModal.loading || creditMemoInvoiceEditor.saving}
                    >
                      {transferDocumentCreditMemoEditing ? creditMemoInvoiceEditor.saving ? 'Saving...' : 'Save Memo' : 'Edit Memo'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="button small red"
                    onClick={transferDocumentCreditMemoEditing ? cancelCreditMemoInvoiceEditor : closeTransferDocumentModal}
                    disabled={transferDocumentCreditMemoEditing && creditMemoInvoiceEditor.saving}
                  >
                    {transferDocumentCreditMemoEditing ? 'Cancel' : 'Close'}
                  </button>
                </div>
              </div>
              {transferDocumentModal.loading && <p className="invoice-modal-status">Loading credit memo...</p>}
              {!transferDocumentModal.loading && transferDocumentModal.error && (
                <p className="invoice-modal-status error">{transferDocumentModal.error}</p>
              )}
              {transferDocumentCreditMemoEditing && creditMemoInvoiceEditor.error && (
                <p className="shipdoc-invoice-edit-error">{creditMemoInvoiceEditor.error}</p>
              )}
              {!transferDocumentModal.loading && transferDocumentModal.transfer && (
                <section className="shipdoc-invoice-workspace">
                  <ShipDocInvoiceDocument
                    transfer={transferDocumentModal.transfer}
                    editable={transferDocumentCreditMemoEditing}
                    editDraft={transferDocumentCreditMemoEditing ? creditMemoInvoiceEditor.draft : null}
                    taxOptions={invoiceEditTaxOptions(0, creditMemoInvoiceEditor.draft?.taxRate)}
                    onDraftItemChange={updateCreditMemoInvoiceDraftItem}
                    onDraftMemoChange={updateCreditMemoInvoiceDraftMemo}
                    onDraftTaxRateChange={updateCreditMemoInvoiceDraftTaxRate}
                    canEditAmounts={false}
                    canEditTax={false}
                  />
                </section>
              )}
            </div>
          </div>
        )}
      </main>
    </>
  );
}

export default RepairOrdersPage;
